import React, { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import ReactMarkdown from 'react-markdown';
import { getErrorMessage } from '../utils/errorUtils';
import Sidebar from '../components/Sidebar';
import { useSidebar } from '../contexts/SidebarContext';
import ContractAnalysisEditor, { ContractAnalysisEditorRef } from '../components/ContractAnalysisEditor';
import ContractPreviewPatch from '../components/ContractPreviewPatch';
import {
  Container,
  Typography,
  Box,
  Paper,
  Alert,
  Button,
  CircularProgress,
  Chip,
  Card,
  CardContent,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  LinearProgress,
  IconButton,
  Tooltip,
  Fade,
  TextField,
  Avatar,
  Dialog,
  DialogContent,
  Stack,
  Grow,
  Snackbar,
  useTheme,
  useMediaQuery,
  Tabs,
  Tab,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  Description as FileIcon,
  Warning as WarningIcon,
  CheckCircle as CheckIcon,
  Error as ErrorIcon,
  Info as InfoIcon,
  Download as DownloadIcon,
  Security as SecurityIcon,
  Speed as SpeedIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  AddComment as AddCommentIcon,
  Comment as CommentIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  TrackChanges as TrackChangesIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Share as ShareIcon,
  Search as SearchIcon,
  Assessment as AssessmentIcon,
  VpnKey as KeyIcon,
  Assignment as AssignmentIcon,
  TaskAlt as TaskAltIcon
} from '@mui/icons-material';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import CloseIcon from '@mui/icons-material/Close';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import { contractsService } from '../services/contractsService';
import SelectionToolbar from '../components/SelectionToolbar';
import { api } from '../services/api';
import { PAY_PER_USE_PRICING, CREDIT_VALUE_CZK } from '../config/pricing';

interface RiskFactor {
  category: string;
  severity: 'low' | 'medium' | 'high';
  description: string;
  recommendation: string;
  clause_text?: string;
  resolvedByRevision?: boolean;
  revisionId?: string;
}

interface KeyTerm {
  category: string;
  value: string;
  description: string;
  is_standard: boolean;
  recommendation?: string;
}

interface AnalysisResult {
  success: boolean;
  document_type: string;
  risk_score: number;
  summary: string;
  key_terms: KeyTerm[];
  risk_factors: RiskFactor[];
  recommendations: string[];
  overall_assessment: string;
  analysis_date: string;
  processing_time_seconds: number;
  original_text: string;  // Add original text for preview
}

const ContractAnalysisPage: React.FC = () => {
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analysisResult, setAnalysisResult] = useState<AnalysisResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [analysisProgress, setAnalysisProgress] = useState(0);
  const [analysisPhase, setAnalysisPhase] = useState<string>('');
  const [highlightedText, setHighlightedText] = useState<string>('');
  const [leftPanelVisible, setLeftPanelVisible] = useState(true);
  const { collapsed } = useSidebar();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('lg'));
  const [isAddingComment, setIsAddingComment] = useState(false);
  const [selectedText, setSelectedText] = useState<string>('');
  const [selectedTextRange, setSelectedTextRange] = useState<{start: number, end: number} | null>(null);
  // User-selected party position within the contract (UI). Mapped to API role later.
  const [userRole, setUserRole] = useState<'first' | 'second'>('first');
  const [comments, setComments] = useState<Array<{
    id: string;
    text: string;
    selectedText: string;
    start: number;
    end: number;
    comment: string;
    timestamp: Date;
  }>>([]);
  const [revisions, setRevisions] = useState<Array<{
    id: string;
    originalText: string;
    newText: string;
    reason: string;
    start: number;
    end: number;
    timestamp: Date;
    accepted: boolean | null;
    authorSource?: 'original' | 'shared';
  }>>([]);
  const [showCommentDialog, setShowCommentDialog] = useState(false);
  const [showRevisionDialog, setShowRevisionDialog] = useState(false);
  const [newCommentText, setNewCommentText] = useState('');
  const [newRevisionText, setNewRevisionText] = useState('');
  const [newRevisionReason, setNewRevisionReason] = useState('');
  const [isAddingRevision, setIsAddingRevision] = useState(false);
  const [roleConfirmed, setRoleConfirmed] = useState(false);
  const [editingComment, setEditingComment] = useState<string | null>(null);
  const [editCommentText, setEditCommentText] = useState('');
  const [deleteTarget, setDeleteTarget] = useState<{ kind: 'comment' | 'revision', id: string } | null>(null);
  const [confirmAcceptRevision, setConfirmAcceptRevision] = useState<string | null>(null);
  const [rejectRevisionId, setRejectRevisionId] = useState<string | null>(null);
  const [rejectReason, setRejectReason] = useState('');
  const [shareLink, setShareLink] = useState<string | null>(null);
  const [showShareDialog, setShowShareDialog] = useState(false);
  const [currentShareId, setCurrentShareId] = useState<string | null>(null);
  const [showRevisionDetail, setShowRevisionDetail] = useState<string | null>(null);
  const [showRisks, setShowRisks] = useState(true);
  const [hideResolvedRisks, setHideResolvedRisks] = useState(false);
  const [resolvedRisks, setResolvedRisks] = useState<Set<string>>(new Set());
  const [rightPanelVisible, setRightPanelVisible] = useState(true);
  const [linkCopied, setLinkCopied] = useState(false);
  const analysisPricing = PAY_PER_USE_PRICING.find(item => item.key === 'contract-analysis');
  const formatCurrency = (value: number) => value.toLocaleString('cs-CZ');
  const creditValue = CREDIT_VALUE_CZK.toLocaleString('cs-CZ', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const [activeTab, setActiveTab] = useState(0);
  const [activeHeadingId, setActiveHeadingId] = useState<string>('');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const analyzeButtonRef = useRef<HTMLDivElement>(null);
  const contractPreviewRef = useRef<HTMLDivElement>(null);
  const mdxEditorRef = useRef<ContractAnalysisEditorRef>(null);
  const [sideBySide, setSideBySide] = useState(false);
  const previewLeftRef = useRef<HTMLDivElement>(null);
  const previewRightRef = useRef<HTMLDivElement>(null);
  const [quickSelect, setQuickSelect] = useState<{
    text: string;
    start: number;
    end: number;
    x: number;
    y: number;
    visible: boolean;
  } | null>(null);

  // AI risk fix dialog state
  const [fixDialogOpen, setFixDialogOpen] = useState(false);
  const [fixLoading, setFixLoading] = useState(false);
  const [fixError, setFixError] = useState<string | null>(null);
  const [fixText, setFixText] = useState('');
  const [fixReason, setFixReason] = useState('');
  const [fixRiskIndex, setFixRiskIndex] = useState<number | null>(null);
  
  // Precompute risk highlight ranges (default highlighting after analysis)
  const riskHighlightRanges = useMemo(() => {
    try {
      if (!analysisResult?.original_text || !analysisResult?.risk_factors?.length) return [] as Array<{start:number; end:number; idx:number}>;
      const text = analysisResult.original_text;
      const lc = text.toLowerCase();
      const ranges: Array<{start:number; end:number; idx:number}> = [];
      const findSentenceWindow = (pos: number) => {
        const starters = [text.lastIndexOf('.', pos - 1), text.lastIndexOf('\n', pos - 1), text.lastIndexOf('\r', pos - 1), text.lastIndexOf(';', pos - 1)];
        const start = Math.max(0, ...starters) + 1;
        const enders = [text.indexOf('.', pos), text.indexOf('\n', pos), text.indexOf('\r', pos), text.indexOf(';', pos)].filter(n => n >= 0);
        const end = enders.length ? Math.min(...enders) + 1 : Math.min(text.length, pos + 180);
        return { start, end };
      };
      
      analysisResult.risk_factors.forEach((rf, idx) => {
        let added = false;
        const clause = (rf?.clause_text || '').toString().trim();
        if (clause && clause.length >= 8) {
          const pos = lc.indexOf(clause.toLowerCase());
          if (pos >= 0) {
            ranges.push({ start: pos, end: Math.min(pos + clause.length, text.length), idx });
            added = true;
          } else {
            const prefix = clause.substring(0, Math.min(30, clause.length));
            const p2 = lc.indexOf(prefix.toLowerCase());
            if (p2 >= 0) {
              ranges.push({ start: p2, end: Math.min(p2 + Math.max(prefix.length, 80), text.length), idx });
              added = true;
            }
          }
        }
        
        if (!added) {
          const source = `${rf?.category || ''} ${rf?.description || ''}`;
          const tokens = source
            .split(/[^\p{L}\p{N}]+/u)
            .map(s => s.trim())
            .filter(s => s.length >= 6)
            .slice(0, 3);
          for (const t of tokens) {
            const pos = lc.indexOf(t.toLowerCase());
            if (pos >= 0) {
              const w = findSentenceWindow(pos);
              ranges.push({ start: w.start, end: w.end, idx });
              added = true;
              break;
            }
          }
        }
      });
      
      // Deduplicate overlapping/identical ranges
      ranges.sort((a,b) => a.start - b.start);
      const compact: typeof ranges = [];
      ranges.forEach(r => {
        const last = compact[compact.length - 1];
        if (!last || r.start >= last.end) compact.push(r);
      });
      return compact;
    } catch {
      return [] as Array<{start:number; end:number; idx:number}>;
    }
  }, [analysisResult]);

  // Compute displayed risk score after accepted/resolved risks
  const displayedRiskScore = useMemo(() => {
    if (!analysisResult) return 0;
    let deduction = 0;
    analysisResult.risk_factors?.forEach((rf, idx) => {
      const resolved = resolvedRisks.has(`risk-${idx}`) || (rf as any).resolvedByRevision;
      if (resolved) {
        deduction += rf.severity === 'high' ? 30 : rf.severity === 'medium' ? 15 : 5;
      }
    });
    return Math.max(0, (analysisResult.risk_score || 0) - deduction);
  }, [analysisResult, resolvedRisks]);

  const handleFileSelect = useCallback((file: File) => {
    // Validate file type - Full support for PDF, DOCX, TXT
    const allowedTypes = ['application/pdf', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'text/plain'];
    if (!allowedTypes.includes(file.type) && !file.name.match(/\.(pdf|docx|txt)$/i)) {
      setError('Nepodporovaný formát souboru. Povolené: PDF, DOCX, TXT');
      return;
    }

    // Validate file size (increased to 20MB for longer contracts)
    if (file.size > 20 * 1024 * 1024) {
      setError('Soubor je příliš velký. Maximum je 20MB');
      return;
    }

    setUploadedFile(file);
    setError(null);
    setAnalysisResult(null);
    setRoleConfirmed(false);
  }, []);

  // Quick actions toolbar handlers
  const openQuickComment = () => {
    if (!quickSelect) return;
    setSelectedText(quickSelect.text);
    setSelectedTextRange({ start: quickSelect.start, end: quickSelect.end });
    setShowCommentDialog(true);
    setQuickSelect(null);
  };
  const openQuickRevision = () => {
    if (!quickSelect) return;
    setSelectedText(quickSelect.text);
    setSelectedTextRange({ start: quickSelect.start, end: quickSelect.end });
    setNewRevisionText(quickSelect.text);
    setShowRevisionDialog(true);
    setQuickSelect(null);
  };
  const [rephraseVariant, setRephraseVariant] = useState<'conservative' | 'standard' | 'aggressive' | null>(null);
  const openQuickRephrase = (variant?: 'conservative' | 'standard' | 'aggressive') => {
    if (!quickSelect) return;
    setSelectedText(quickSelect.text);
    setSelectedTextRange({ start: quickSelect.start, end: quickSelect.end });
    setNewRevisionText(quickSelect.text);
    // uložíme zvolenou variantu pro další krok/referenční použití
    setRephraseVariant(variant ?? 'standard');
    setShowRevisionDialog(true);
    setQuickSelect(null);
  };

  // Auto scroll to analyze button when file is uploaded
  useEffect(() => {
    if (uploadedFile && !isAnalyzing && !analysisResult && analyzeButtonRef.current) {
      // Small delay to allow the button to render and animate in
      const timer = setTimeout(() => {
        analyzeButtonRef.current?.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'center',
          inline: 'nearest'
        });
      }, 500); // Wait for Fade animation to complete
      
      return () => clearTimeout(timer);
    }
  }, [uploadedFile, isAnalyzing, analysisResult]);

  // Synchronizace s shared verzí - ukládání změn
  useEffect(() => {
    if (currentShareId && analysisResult?.original_text) {
      const sharedData = {
        id: currentShareId,
        originalText: analysisResult.original_text,
        createdAt: new Date().toISOString(),
        comments: comments,
        revisions: revisions
      };
      localStorage.setItem(`shared-contract-${currentShareId}`, JSON.stringify(sharedData));
    }
  }, [comments, revisions, currentShareId, analysisResult]);

  // Synchronizace s shared verzí - načítání změn
  useEffect(() => {
    if (!currentShareId) return;
    
    const loadSharedChanges = () => {
      const storedData = localStorage.getItem(`shared-contract-${currentShareId}`);
      if (storedData) {
        const parsedData = JSON.parse(storedData);
        // Aktualizujeme pouze pokud se data změnila
        if (JSON.stringify(parsedData.comments) !== JSON.stringify(comments)) {
          setComments(parsedData.comments || []);
        }
        if (JSON.stringify(parsedData.revisions) !== JSON.stringify(revisions)) {
          setRevisions(parsedData.revisions || []);
        }
      }
    };
    
    // Načítáme změny každé 2 sekundy
    const interval = setInterval(loadSharedChanges, 2000);
    
    return () => clearInterval(interval);
  }, [currentShareId, comments, revisions]);

  // Track active heading for TOC highlighting
  useEffect(() => {
    if (activeTab !== 1 || !analysisResult?.overall_assessment) return;

    const handleScroll = () => {
      const headings = document.querySelectorAll('h3[id]');
      let currentHeading = '';

      headings.forEach((heading) => {
        const rect = heading.getBoundingClientRect();
        if (rect.top <= 150 && rect.top >= -rect.height) {
          currentHeading = heading.id;
        }
      });

      setActiveHeadingId(currentHeading);
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll(); // Initial check

    return () => window.removeEventListener('scroll', handleScroll);
  }, [activeTab, analysisResult]);

  // Handle clicking on revision to navigate without overriding display
  const handleRevisionClick = (revision: any) => {
    // Open edit dialog for revision
    setEditingComment(revision.id);
    setSelectedText(revision.originalText);
    setSelectedTextRange({ start: revision.start, end: revision.end });
    setNewRevisionText(revision.newText || '');
    setNewRevisionReason(revision.reason || '');
    setShowRevisionDialog(true);
    setIsAddingRevision(false);
  };

  const handleCommentClick = (comment: any) => {
    // Open edit dialog for comment
    setEditingComment(comment.id);
    setSelectedText(comment.selectedText || '');
    setSelectedTextRange({ start: comment.start, end: comment.end });
    setNewCommentText(comment.comment || comment.text || '');
    setShowCommentDialog(true);
    setIsAddingComment(false);
  };

  const handleDeleteComment = (id: string) => {
    setDeleteTarget({ kind: 'comment', id });
  };

  const handleDeleteRevision = (id: string) => {
    setDeleteTarget({ kind: 'revision', id });
  };

  const handleRiskClick = (riskFactor: RiskFactor, riskIndex: number) => {
    if (!analysisResult?.original_text || !contractPreviewRef.current) return;
    
    // Check if this risk has been resolved
    const riskId = `risk-${riskIndex}`;
    if (resolvedRisks.has(riskId) || riskFactor.resolvedByRevision) {
      // Variant C: Just scroll to the position where the revision was made
      // Find the revision that resolved this risk
      const relatedRevision = revisions.find(r => r.id === riskFactor.revisionId && r.accepted);
      
      if (relatedRevision && contractPreviewRef.current) {
        // Scroll to the position of the revision
        const previewElement = contractPreviewRef.current;
        const textToFind = relatedRevision.originalText.substring(0, Math.min(30, relatedRevision.originalText.length)).toLowerCase();
        const previewContent = (previewElement.textContent || '').toLowerCase();
        const textIndex = previewContent.indexOf(textToFind);

        if (textIndex >= 0) {
          const scrollRatio = textIndex / previewContent.length;
          const scrollPosition = Math.max(0, (scrollRatio * previewElement.scrollHeight) - 100);

          previewElement.scrollTo({
            top: scrollPosition,
            behavior: 'smooth'
          });
        }
      } else if (contractPreviewRef.current) {
        // If no revision found, just scroll to top
        contractPreviewRef.current.scrollTo({ top: 0, behavior: 'smooth' });
      }
      
      // Don't highlight anything for resolved risks
      setHighlightedText('');
      return;
    }
    
    const originalText = analysisResult.original_text;
    let textToHighlight = '';
    
    // Strategie 1: Pokud máme clause_text, zkusíme ho najít
    if (riskFactor.clause_text && riskFactor.clause_text.trim()) {
      const cleanClauseText = riskFactor.clause_text.trim();
      
      // Zkusíme přesnou shodu
      if (originalText.includes(cleanClauseText)) {
        textToHighlight = cleanClauseText;
      }
      // Zkusíme první část textu (case insensitive)
      else {
        const firstWords = cleanClauseText.split(' ').slice(0, 5).join(' ');
        const lowerOriginal = originalText.toLowerCase();
        const lowerSearch = firstWords.toLowerCase();
        const startIndex = lowerOriginal.indexOf(lowerSearch);
        
        if (startIndex >= 0) {
          // Najdeme konec věty nebo alespoň 100 znaků
          const endIndex = originalText.indexOf('.', startIndex + firstWords.length);
          textToHighlight = originalText.substring(startIndex, endIndex > 0 ? endIndex + 1 : startIndex + 100);
        }
      }
    }
    
    // Strategie 2: Hledáme podle klíčových slov z kategorie
    if (!textToHighlight) {
      const categoryKeywords = riskFactor.category.toLowerCase().split(' ').filter(w => w.length > 4);
      const descriptionKeywords = riskFactor.description.toLowerCase().split(' ').filter(w => w.length > 5).slice(0, 2);
      const allKeywords = [...categoryKeywords, ...descriptionKeywords];
      
      for (const keyword of allKeywords) {
        const searchIndex = originalText.toLowerCase().indexOf(keyword);
        if (searchIndex >= 0) {
          // Najdeme začátek věty
          let sentenceStart = originalText.lastIndexOf('.', searchIndex - 1);
          sentenceStart = sentenceStart > 0 ? sentenceStart + 1 : 0;
          
          // Najdeme konec věty
          let sentenceEnd = originalText.indexOf('.', searchIndex);
          sentenceEnd = sentenceEnd > 0 ? sentenceEnd + 1 : Math.min(originalText.length, searchIndex + 150);
          
          textToHighlight = originalText.substring(sentenceStart, sentenceEnd).trim();
          break;
        }
      }
    }
    
    // Strategie 3: Pokud nic nenajdeme, zkusíme obecná klíčová slova
    if (!textToHighlight) {
      const commonTerms = ['cena', 'platba', 'záruka', 'pokuta', 'lhůta', 'odpovědnost', 'výpověď'];
      for (const term of commonTerms) {
        const searchIndex = originalText.toLowerCase().indexOf(term);
        if (searchIndex >= 0) {
          const startIndex = Math.max(0, searchIndex - 30);
          const endIndex = Math.min(originalText.length, searchIndex + 100);
          textToHighlight = originalText.substring(startIndex, endIndex);
          break;
        }
      }
    }
    
    // Nastavíme zvýraznění
    if (textToHighlight) {
      setHighlightedText(textToHighlight);
      
      // Scrollování na zvýrazněný text
      setTimeout(() => {
        const previewElement = contractPreviewRef.current;
        if (previewElement) {
          const textToFind = textToHighlight.substring(0, 30).toLowerCase();
          const previewContent = (previewElement.textContent || '').toLowerCase();
          const textIndex = previewContent.indexOf(textToFind);
          
          if (textIndex >= 0) {
            const scrollRatio = textIndex / previewContent.length;
            const scrollPosition = Math.max(0, (scrollRatio * previewElement.scrollHeight) - 100);
            previewElement.scrollTo({
              top: scrollPosition,
              behavior: 'smooth'
            });
          }
        }
      }, 100);
    } else {
      // Pokud nemůžeme najít text, aspoň scrollujeme nahoru
      setHighlightedText('');
      if (contractPreviewRef.current) {
        contractPreviewRef.current.scrollTo({ top: 0, behavior: 'smooth' });
      }
    }
  };

  const handleGenerateShareLink = () => {
    if (!analysisResult?.original_text) return;
    // Generujeme unikátní ID pro sdílení
    const shareId = Date.now().toString(36) + Math.random().toString(36).substr(2);
    // Uložíme data do localStorage
    const sharedData = {
      id: shareId,
      originalText: analysisResult.original_text,
      createdAt: new Date().toISOString(),
      comments: comments,
      revisions: revisions
    };
    localStorage.setItem(`shared-contract-${shareId}`, JSON.stringify(sharedData));
    // Uložíme ID pro synchronizaci a vytvoříme odkaz
    setCurrentShareId(shareId);
    const link = `${window.location.origin}/shared/${shareId}`;
    setShareLink(link);
    // Rovnou zkopírujeme pro pohodlí
    try { navigator.clipboard.writeText(link); } catch {}
    setLinkCopied(true);
    setTimeout(() => setLinkCopied(false), 3000);
  };

  const handleCopyShareLink = () => {
    if (shareLink) {
      navigator.clipboard.writeText(shareLink);
    }
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const handleDragOver = (event: React.DragEvent) => {
    event.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (event: React.DragEvent) => {
    event.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (event: React.DragEvent) => {
    event.preventDefault();
    setIsDragOver(false);
    
    const file = event.dataTransfer.files[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const openFileDialog = () => {
    fileInputRef.current?.click();
  };

  const analyzeContract = async () => {
    if (!uploadedFile) return;

    setIsAnalyzing(true);
    setError(null);
    setAnalysisProgress(0);
    setAnalysisPhase('Připravuji analýzu...');

    // Simulace průběhu analýzy
    const progressInterval = setInterval(() => {
      setAnalysisProgress((prev) => {
        if (prev >= 90) return prev;
        return prev + Math.random() * 15;
      });
    }, 800);

    const phases = [
      'Načítám dokument...',
      'Extrakce textu ze všech stránek...',
      'Identifikace typu smlouvy...',
      'Analýza právních rizik...',
      'Kontrola asymetrie a nevyvážeností...',
      'Hodnocení finančních dopadů...',
      'Kontrola souladu s českým právem...',
      'Vyhledávání v judikatech...',
      'Benchmarking proti tržní praxi...',
      'Generování alternativních formulací...',
      'Příprava akčního plánu...',
      'Finalizace kompletní analýzy...'
    ];

    let phaseIndex = 0;
    const phaseInterval = setInterval(() => {
      if (phaseIndex < phases.length) {
        setAnalysisPhase(phases[phaseIndex]);
        phaseIndex++;
      }
    }, 2000);

    try {
      const formData = new FormData();
      formData.append('file', uploadedFile);
      // Map selected position to API role aliases (heuristic)
      const apiPartyRole = userRole === 'first' ? 'odberatel' : 'zhotovitel';
      formData.append('party_role', apiPartyRole);

      // Prefer authenticated endpoint (persists to history reliably); fallback to test endpoint
      let result: any;
      try {
        const res = await api.post('/contracts/analyze-contract', formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
          withCredentials: true,
        });
        result = res.data;
      } catch (e: any) {
        // Fallback to test endpoint if auth required or other issues
        const apiBaseUrl = process.env.REACT_APP_API_URL || '/api/v1';
        const resp = await fetch(`${apiBaseUrl}/contracts/test/analyze-contract`, {
          method: 'POST',
          body: formData,
          credentials: 'include',
        });
        if (!resp.ok) {
          const errorData = await resp.json().catch(() => ({ detail: 'Analýza se nezdařila' }));
          throw new Error(getErrorMessage(errorData.detail, 'Analýza se nezdařila'));
        }
        result = await resp.json();
      }
      
      setAnalysisProgress(100);
      setAnalysisPhase('Analýza dokončena!');
      
      setTimeout(() => {
        setAnalysisResult(result);
      }, 500);
    } catch (err) {
      setError(getErrorMessage(err, 'Došlo k neočekávané chybě'));
    } finally {
      clearInterval(progressInterval);
      clearInterval(phaseInterval);
      setTimeout(() => {
        setIsAnalyzing(false);
        setAnalysisProgress(0);
        setAnalysisPhase('');
      }, 500);
    }
  };

  // Klávesové zkratky pro rychlé akce: C (komentář), R (revize), P (přeformulovat)
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (!quickSelect?.visible) return;
      const key = e.key.toLowerCase();
      if (key === 'c') {
        e.preventDefault();
        openQuickComment();
      } else if (key === 'r') {
        e.preventDefault();
        openQuickRevision();
      } else if (key === 'p') {
        e.preventDefault();
        openQuickRephrase('standard');
      } else if (key === 'escape') {
        setQuickSelect(null);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [quickSelect]);

  const getRiskColor = (score: number) => {
    if (score >= 70) return '#f44336'; // red
    if (score >= 40) return '#ff9800'; // orange
    return '#4caf50'; // green
  };

  const getRiskLabel = (score: number) => {
    if (score >= 70) return 'VYSOKÉ RIZIKO';
    if (score >= 40) return 'STŘEDNÍ RIZIKO';
    return 'NÍZKÉ RIZIKO';
  };

  const handleSuggestFix = async (risk: RiskFactor, index: number) => {
    if (!analysisResult?.original_text) return;
    setFixDialogOpen(true);
    setFixLoading(true);
    setFixError(null);
    setFixText('');
    setFixReason('');
    setFixRiskIndex(index);
    try {
      const res = await contractsService.suggestRiskFix({
        original_text: analysisResult.original_text,
        risk: {
          category: risk.category,
          severity: risk.severity,
          description: risk.description,
          recommendation: risk.recommendation,
          clause_text: risk.clause_text,
        },
        party_role: userRole,
      });
      setFixText(res.new_text);
      setFixReason(res.reason);
    } catch (e: any) {
      setFixError(getErrorMessage(e, 'Nepodařilo se získat návrh opravy'));
    } finally {
      setFixLoading(false);
    }
  };

  const applySuggestedFix = () => {
    if (!analysisResult?.original_text || fixRiskIndex === null || !fixText) {
      setFixDialogOpen(false);
      return;
    }

    const risk = analysisResult.risk_factors[fixRiskIndex];
    const doc = analysisResult.original_text;
    let start = -1;
    let end = -1;

    // Prefer precomputed range for this risk
    const range = riskHighlightRanges.find(r => r.idx === fixRiskIndex);
    if (range) {
      start = range.start;
      end = range.end;
    } else if (risk.clause_text) {
      const pos = doc.toLowerCase().indexOf(risk.clause_text.toLowerCase());
      if (pos >= 0) {
        start = pos;
        end = pos + risk.clause_text.length;
      }
    }

    // If not found, fallback to no-op
    if (start < 0 || end <= start) {
      // As a fallback, do not create a broken revision
      setFixDialogOpen(false);
      return;
    }

    const originalSegment = doc.substring(start, end);
    const rev = {
      id: `${Date.now()}`,
      originalText: originalSegment,
      newText: fixText,
      reason: fixReason || `AI návrh opravy rizika: ${risk.category}`,
      start,
      end,
      timestamp: new Date(),
      accepted: true as const,
      authorSource: 'original' as const,
    };
    setRevisions(prev => [...prev, rev]);

    // Mark risk as resolved
    const newSet = new Set(resolvedRisks);
    newSet.add(`risk-${fixRiskIndex}`);
    setResolvedRisks(newSet);
    if (newSet.size > resolvedRisks.size) {
      setHideResolvedRisks(true);
    }

    // Update risk flag in analysisResult for UI
    try {
      const clone = JSON.parse(JSON.stringify(analysisResult));
      clone.risk_factors[fixRiskIndex].resolvedByRevision = true;
      setAnalysisResult(clone);
    } catch {}

    setFixDialogOpen(false);
  };

  const getSeverityIcon = (severity: string) => {
    switch (severity) {
      case 'high':
        return <ErrorIcon color="error" />;
      case 'medium':
        return <WarningIcon color="warning" />;
      case 'low':
        return <InfoIcon color="info" />;
      default:
        return <InfoIcon />;
    }
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'high':
        return 'error';
      case 'medium':
        return 'warning';
      case 'low':
        return 'info';
      default:
        return 'default';
    }
  };

  return (
    <Box sx={{ display: 'flex' }}>
      <Sidebar />
      
      <Box sx={{ 
        flex: 1,
        marginLeft: isMobile ? 0 : (collapsed ? '80px' : '280px'),
        paddingTop: isMobile ? '64px' : 0,
        minHeight: '100vh',
        backgroundColor: '#F2F2F2',
        transition: 'margin-left 0.3s ease'
      }}>
        <Box sx={{ 
          px: { xs: 3, sm: 4, md: 6 },
          py: { xs: 4, sm: 6 }
        }}>
        
        {/* Debug button for panel toggle */}
        {/* Header - hide when results are displayed */}
        {!analysisResult && (
        <Fade in timeout={800}>
          <Box sx={{
            textAlign: 'center',
            mb: 10,
            position: 'relative'
          }}>
            <Box sx={{
              position: 'absolute',
              top: '-20%',
              left: '50%',
              transform: 'translateX(-50%)',
              width: '500px',
              height: '500px',
              background: 'radial-gradient(circle, rgba(255,255,255,0.3) 0%, transparent 70%)',
              filter: 'blur(100px)',
              pointerEvents: 'none',
              zIndex: -1
            }} />
            
            <Avatar sx={{ 
              width: 100, 
              height: 100, 
              mx: 'auto', 
              mb: 4,
              background: 'linear-gradient(135deg, rgba(255,255,255,0.9) 0%, rgba(255,255,255,0.7) 100%)',
              backdropFilter: 'blur(16px)',
              WebkitBackdropFilter: 'blur(16px)',
              border: '1px solid rgba(255,255,255,0.4)',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.04), 0 4px 16px rgba(0, 0, 0, 0.05)',
              '& svg': {
                color: '#424242'
              }
            }}>
              <SecurityIcon sx={{ fontSize: 50 }} />
            </Avatar>
            
            <Typography 
              variant="h1" 
              component="h1" 
              gutterBottom 
              sx={{ 
                fontWeight: 700,
                fontSize: { xs: '3rem', md: '4rem' },
                mb: 3,
                color: '#212121',
                letterSpacing: '-0.02em'
              }}
            >
              Analýza smluv
            </Typography>
            
            <Typography 
              variant="h5" 
              sx={{ 
                maxWidth: 700,
                mx: 'auto',
                mb: 4,
                fontWeight: 400,
                color: '#424242',
                letterSpacing: '-0.01em'
              }}
            >
              Nahrajte smlouvu a získejte detailní analýzu rizik s AI asistencí
            </Typography>

            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mb: 5, flexWrap: 'wrap' }}>
              <Chip 
                icon={<CheckIcon sx={{ fontSize: 18 }} />}
                label="PDF, DOCX, TXT" 
                sx={{ 
                  background: 'rgba(255,255,255,0.8)',
                  backdropFilter: 'blur(8px)',
                  WebkitBackdropFilter: 'blur(8px)',
                  border: '1px solid rgba(255,255,255,0.3)',
                  color: '#424242',
                  fontWeight: 500,
                  px: 2,
                  py: 2.5
                }} 
              />
              <Chip 
                icon={<SpeedIcon sx={{ fontSize: 18 }} />}
                label="AI Powered" 
                sx={{ 
                  background: 'rgba(255,255,255,0.8)',
                  backdropFilter: 'blur(8px)',
                  WebkitBackdropFilter: 'blur(8px)',
                  border: '1px solid rgba(255,255,255,0.3)',
                  color: '#424242',
                  fontWeight: 500,
                  px: 2,
                  py: 2.5
                }} 
              />
            <Chip 
              icon={<SecurityIcon sx={{ fontSize: 18 }} />}
              label="Bezpečné" 
              sx={{ 
                background: 'rgba(255,255,255,0.8)',
                backdropFilter: 'blur(8px)',
                WebkitBackdropFilter: 'blur(8px)',
                border: '1px solid rgba(255,255,255,0.3)',
                color: '#424242',
                fontWeight: 500,
                px: 2,
                py: 2.5
              }} 
            />
            {analysisPricing && (
              <Chip
                icon={<AssignmentIcon sx={{ fontSize: 18 }} />}
                label={`${formatCurrency(analysisPricing.credits)} kreditů / ${formatCurrency(analysisPricing.priceCZK)} Kč`}
                sx={{ 
                  background: 'rgba(231,47,13,0.12)',
                  color: '#e72f0d',
                  fontWeight: 600,
                  border: '1px solid rgba(231,47,13,0.2)',
                  px: 2,
                  py: 2.5
                }}
              />
            )}
            </Box>
            {analysisPricing && (
              <Typography sx={{ fontSize: '13px', color: '#424242', textAlign: 'center', mt: -2 }}>
                1 kredit = {creditValue} Kč. Platba za analýzu se odečte až po doručení výsledku.
              </Typography>
            )}
          </Box>
        </Fade>
        )}


        {/* Step Cards - hide after analysis is completed */}
        {!analysisResult && (
          <Box sx={{ display: 'flex', gap: 3, mb: 4 }}>
          {/* Step 1 Card */}
          <Paper
            onClick={openFileDialog}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            role="button"
            tabIndex={0}
            sx={{
              p: 3,
              flex: 1,
              border: '2px solid',
              borderColor: uploadedFile ? 'success.main' : (isDragOver ? 'primary.main' : 'primary.main'),
              bgcolor: uploadedFile ? 'success.50' : (isDragOver ? 'action.hover' : 'primary.50'),
              position: 'relative',
              cursor: 'pointer'
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                openFileDialog();
              }
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
              <Box sx={{
                width: 32,
                height: 32,
                borderRadius: '50%',
                bgcolor: uploadedFile ? '#4caf50' : '#d32f2f',
                color: '#fff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontWeight: 'bold',
                fontSize: '1rem'
              }}>
                {uploadedFile ? '✓' : '1'}
              </Box>
              <Typography variant="h6" sx={{ fontWeight: 600, color: '#000' }}>
                Krok 1: Nahrajte smlouvu
              </Typography>
            </Box>
            {/* Hidden input for upload */}
            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf,.docx,.txt"
              onChange={handleFileChange}
              style={{ display: 'none' }}
            />
            {uploadedFile ? (
              <>
                <Box sx={{ textAlign: 'center' }}>
                  <CheckIcon sx={{ fontSize: 60, color: 'success.main', mb: 2 }} />
                  <Typography variant="h6" gutterBottom sx={{ color: '#333333', fontWeight: 600 }}>
                    ✅ Soubor byl úspěšně nahrán!
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2, mb: 2, flexWrap: 'wrap' }}>
                    <Chip
                      icon={<FileIcon sx={{ color: '#333333 !important' }} />}
                      label={`${uploadedFile.name}`}
                      sx={{ fontWeight: 600, backgroundColor: '#e8f5e8', color: '#333333 !important', border: '1px solid #4caf50', '& .MuiChip-label': { color: '#333333 !important' } }}
                    />
                    <Chip
                      label={`${(uploadedFile.size / 1024 / 1024).toFixed(2)} MB`}
                      variant="outlined"
                      sx={{ borderColor: '#4caf50', color: '#333333 !important', '& .MuiChip-label': { color: '#333333 !important' } }}
                    />
                  </Box>
                  <Typography variant="body2" sx={{ color: '#333333', mb: 2 }}>
                    📄 Soubor je připraven k analýze
                  </Typography>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      setUploadedFile(null);
                      setError(null);
                      setAnalysisResult(null);
                    }}
                    sx={{ borderColor: '#4caf50', color: '#333333 !important', backgroundColor: 'rgba(255,255,255,0.8)', '&:hover': { borderColor: '#4caf50', backgroundColor: 'rgba(255,255,255,0.9)', color: '#333333 !important' } }}
                  >
                    Nahrát jiný soubor
                  </Button>
                </Box>
              </>
            ) : (
              <Box sx={{ textAlign: 'center' }}>
                <UploadIcon sx={{ fontSize: 60, color: '#d32f2f', mb: 2 }} />
                <Typography variant="h6" gutterBottom sx={{ color: '#000', fontWeight: 600 }}>
                  {isDragOver ? 'Přetáhněte soubor sem...' : 'Klikněte zde pro výběr souboru nebo přetáhněte'}
                </Typography>
                <Typography variant="body2" sx={{ color: '#666' }}>
                  Podporované formáty: PDF, DOCX, TXT (max. 20MB)
                </Typography>
                <Typography variant="caption" sx={{ display: 'block', mt: 1, color: 'success.main', fontWeight: 600 }}>
                  ✓ Podporujeme vícestránkové dokumenty - celá smlouva bude analyzována
                </Typography>
              </Box>
            )}
          </Paper>

          {/* Step 2 Card - Choose perspective */}
          <Paper sx={{
            p: 3,
            flex: 1,
            border: '2px solid',
            borderColor: !uploadedFile ? 'grey.300' : roleConfirmed ? 'success.main' : 'warning.main',
            bgcolor: !uploadedFile ? 'grey.50' : roleConfirmed ? 'success.50' : 'warning.50',
            opacity: uploadedFile ? 1 : 0.6
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
              <Box sx={{
                width: 32,
                height: 32,
                borderRadius: '50%',
                bgcolor: !uploadedFile ? 'grey.400' : roleConfirmed ? 'success.main' : 'warning.main',
                color: 'white',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontWeight: 'bold'
              }}>
                {!uploadedFile ? '2' : roleConfirmed ? '✓' : '2'}
              </Box>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Krok 2: Zvolte pořadí strany
              </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary">
              {!uploadedFile ? 'Nejdříve nahrajte smlouvu' :
               roleConfirmed ? `✓ Zvoleno: ${userRole === 'first' ? 'První strana' : 'Druhá strana'}` :
               'Vyberte, zda jste ve smlouvě uvedeni jako první nebo druhá strana'}
            </Typography>

            {/* Role selector inside Step 2 */}
            <Box sx={{ mt: 1.5, display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  Zvolená strana
                </Typography>
                <Tooltip
                  title="AI bude při analýze hájit zájmy zvolené strany (první nebo druhé uvedené ve smlouvě) a navrhovat úpravy v její prospěch."
                  arrow
                >
                  <HelpOutlineIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                </Tooltip>
              </Box>
              <ToggleButtonGroup
                value={userRole}
                exclusive
                onChange={(_, val) => { if (val) { setUserRole(val); setRoleConfirmed(true); } }}
                size="small"
                disabled={!uploadedFile || isAnalyzing || !!analysisResult}
                sx={{
                  '& .MuiToggleButton-root': {
                    borderRadius: 9999,
                    px: 1.5,
                    py: 0.5,
                    borderColor: 'rgba(0,0,0,0.12)'
                  },
                  '& .Mui-selected': {
                    bgcolor: 'rgba(0,0,0,0.06)',
                    borderColor: 'rgba(0,0,0,0.18)'
                  }
                }}
              >
                <ToggleButton value="first">První strana</ToggleButton>
                <ToggleButton value="second">Druhá strana</ToggleButton>
              </ToggleButtonGroup>
            </Box>
          </Paper>
          {/* Step 3 Card - Wait for analysis */}
          <Paper sx={{
            p: 3,
            flex: 1,
            border: '2px solid',
            borderColor: analysisResult ? 'success.main' : isAnalyzing ? 'info.main' : 'grey.300',
            bgcolor: analysisResult ? 'success.50' : isAnalyzing ? 'info.50' : 'grey.50',
            opacity: uploadedFile ? 1 : 0.6
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
              <Box sx={{
                width: 32,
                height: 32,
                borderRadius: '50%',
                bgcolor: analysisResult ? 'success.main' : isAnalyzing ? 'info.main' : 'grey.400',
                color: 'white',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontWeight: 'bold'
              }}>
                {analysisResult ? '✓' : isAnalyzing ? '⟳' : '3'}
              </Box>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Krok 3: Počkejte na analýzu
              </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary">
              {analysisResult ? '✓ Analýza dokončena' :
               isAnalyzing ? 'Probíhá analýza smlouvy...' :
               (uploadedFile && roleConfirmed) ? 'Klikněte na "Analyzovat smlouvu" pro spuštění' : 'Nejdříve nahrajte a zvolte pořadí strany'}
            </Typography>
          </Paper>
          </Box>
        )}

        {/* Upload Area removed – handled by Step 1 card */}

        {/* Main Analyze Button - outside upload card */}
        {uploadedFile && !isAnalyzing && !analysisResult && (
          <Fade in>
            <Box ref={analyzeButtonRef} sx={{ textAlign: 'center', mb: 4 }}>
              <Button
                variant="contained"
                size="large"
                onClick={analyzeContract}
                startIcon={<SearchIcon />}
                sx={{
                  py: 1.5,
                  px: 4,
                  fontSize: '14px',
                  fontWeight: 600,
                  bgcolor: '#1F1F1F !important',
                  backgroundColor: '#1F1F1F !important',
                  background: '#1F1F1F !important',
                  color: '#FFFFFF !important',
                  textTransform: 'none',
                  border: 'none !important',
                  boxShadow: '0 4px 12px rgba(31, 31, 31, 0.15)',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    bgcolor: '#e72f0d !important',
                    backgroundColor: '#e72f0d !important',
                    background: '#e72f0d !important',
                    transform: 'translateY(-2px)',
                    boxShadow: '0 8px 20px rgba(231, 47, 13, 0.25)'
                  },
                  '& .MuiButton-startIcon': {
                    '& svg': {
                      color: '#FFFFFF',
                      fontSize: '1.2rem'
                    }
                  }
                }}
              >
                Analyzovat smlouvu
              </Button>
              <Typography 
                variant="body2" 
                sx={{ 
                  mt: 2,
                  color: '#666666',
                  fontWeight: 500
                }}
              >
                Klikněte pro spuštění komplexní právní analýzy
              </Typography>
            </Box>
          </Fade>
        )}

        {/* Loading */}
        {isAnalyzing && (
          <Fade in>
            <Card className="liquid-card" sx={{ 
              p: 6, 
              textAlign: 'center', 
              mb: 4,
              position: 'relative',
              overflow: 'visible',
              background: 'linear-gradient(135deg, rgba(255,255,255,0.95) 0%, rgba(255,255,255,0.85) 100%)',
              backdropFilter: 'blur(16px)',
              WebkitBackdropFilter: 'blur(16px)',
              border: '1px solid rgba(255,255,255,0.2)',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.04), 0 4px 16px rgba(0, 0, 0, 0.05)',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: '-30px',
                left: '50%',
                transform: 'translateX(-50%)',
                width: '200px',
                height: '200px',
                background: 'radial-gradient(circle, rgba(255,255,255,0.4) 0%, transparent 70%)',
                filter: 'blur(80px)',
                pointerEvents: 'none',
                zIndex: -1
              }
            }}>
              <Box sx={{
                mb: 3,
                position: 'relative'
              }}>
                <CircularProgress 
                  size={80} 
                  thickness={3}
                  sx={{ 
                    color: '#424242',
                    animation: 'liquid-float 2s ease-in-out infinite'
                  }} 
                />
              </Box>
              
              <Typography 
                variant="h5" 
                sx={{
                  fontWeight: 600,
                  mb: 2,
                  color: '#212121'
                }}
              >
                🔍 Analyzuji smlouvu...
              </Typography>
              
              <Typography 
                variant="body1" 
                sx={{
                  color: '#424242',
                  mb: 4,
                  maxWidth: '500px',
                  mx: 'auto',
                  lineHeight: 1.6
                }}
              >
                Prověřuji rizika, klíčové podmínky a soulad s českým právem
              </Typography>
              
              <Box sx={{ 
                position: 'relative',
                maxWidth: '400px',
                mx: 'auto'
              }}>
                <LinearProgress 
                  sx={{ 
                    height: 8,
                    borderRadius: 4,
                    backgroundColor: 'rgba(255,255,255,0.6)',
                    '& .MuiLinearProgress-bar': {
                      borderRadius: 4,
                      background: 'linear-gradient(90deg, #424242 0%, #212121 100%)'
                    }
                  }} 
                />
                <Typography 
                  variant="caption" 
                  sx={{
                    display: 'block',
                    mt: 1,
                    color: '#424242',
                    fontWeight: 500
                  }}
                >
                  Analýza probíhá...
                </Typography>
              </Box>
            </Card>
          </Fade>
        )}

        {/* Error */}
        {error && (
          <Alert 
            severity={error.includes('není smlouva') ? 'warning' : 'error'} 
            sx={{ mb: 4 }}
          >
            <Typography variant="h6" sx={{ mb: 1 }}>
              {error.includes('není smlouva') ? '⚠️ Neplatný typ dokumentu' : '❌ Chyba při analýze'}
            </Typography>
            <Typography variant="body1" sx={{ mb: 2 }}>
              {error}
            </Typography>
            {error.includes('není smlouva') && (
              <Box sx={{ mt: 2, p: 2, bgcolor: 'rgba(255, 193, 7, 0.1)', borderRadius: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                  💡 Podporované typy smluv:
                </Typography>
                <Typography variant="body2" component="ul" sx={{ pl: 2, mb: 0 }}>
                  <li>Kupní smlouvy</li>
                  <li>Nájemní smlouvy</li>
                  <li>Smlouvy o dílo</li>
                  <li>Pracovní smlouvy</li>
                  <li>Mandátní smlouvy</li>
                  <li>Darovací smlouvy</li>
                  <li>Jiné smluvní dokumenty</li>
                </Typography>
                <Typography variant="body2" sx={{ mt: 1, fontStyle: 'italic' }}>
                  Faktury, účtenky, nabídky a jiné dokumenty nejsou podporovány.
                </Typography>
              </Box>
            )}
          </Alert>
        )}

        {/* Results - 2 Column Layout */}
        {analysisResult && (
          <Fade in>
            <Box sx={{ mb: 4 }}>
              {/* Overall Score - Full Width */}
              <Card sx={{ mb: 3, overflow: 'visible' }}>
                <CardContent sx={{ textAlign: 'center', py: 4 }}>
                  <Box
                    sx={{
                      width: 120,
                      height: 120,
                      borderRadius: '50%',
                      bgcolor: getRiskColor(displayedRiskScore),
                      color: displayedRiskScore >= 40 ? 'white' : '#333333',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mx: 'auto',
                      mb: 2,
                      position: 'relative',
                    }}
                  >
                    <Typography variant="h3" sx={{ fontWeight: 700 }}>
                      {displayedRiskScore}
                    </Typography>
                    <Typography variant="caption">RIZIKO</Typography>
                  </Box>
                  <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
                    {getRiskLabel(displayedRiskScore)}
                  </Typography>
                  <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                    {analysisResult.document_type}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Analýza dokončena za {analysisResult.processing_time_seconds}s
                  </Typography>
                  <Button
                    variant="outlined"
                    startIcon={<UploadIcon />}
                    onClick={() => {
                      setAnalysisResult(null);
                      setUploadedFile(null);
                      setError(null);
                    }}
                    sx={{
                      borderColor: '#d32f2f',
                      color: '#d32f2f',
                      '&:hover': {
                        borderColor: '#b71c1c',
                        backgroundColor: 'rgba(211, 47, 47, 0.04)'
                      }
                    }}
                  >
                    Analyzovat jinou smlouvu
                  </Button>
                </CardContent>
              </Card>

              {/* User Guide Steps */}
              <Card sx={{ mb: 3, background: 'linear-gradient(135deg, rgba(255,255,255,0.95) 0%, rgba(248,249,252,0.9) 100%)' }}>
                <CardContent sx={{ py: 2 }}>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, textAlign: 'center', color: '#333', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                    <AssignmentIcon sx={{ color: '#d32f2f' }} /> Jak pracovat s výsledky
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', justifyContent: 'center' }}>
                    <Box sx={{ flex: '1 1 220px', textAlign: 'center', p: 1.5, bgcolor: 'rgba(255,255,255,0.6)', borderRadius: 2 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#d32f2f', mb: 0.5, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                        <FileIcon sx={{ fontSize: '1.1rem' }} /> Krok 1: Náhled
                      </Typography>
                      <Typography variant="body2" sx={{ fontSize: '0.85rem', color: '#666' }}>
                        Zobrazte smlouvu, přidávejte poznámky, revize a sledujte rizika
                      </Typography>
                    </Box>
                    <Box sx={{ flex: '1 1 220px', textAlign: 'center', p: 1.5, bgcolor: 'rgba(255,255,255,0.6)', borderRadius: 2 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#d32f2f', mb: 0.5, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                        <AssessmentIcon sx={{ fontSize: '1.1rem' }} /> Krok 2: Rozbor
                      </Typography>
                      <Typography variant="body2" sx={{ fontSize: '0.85rem', color: '#666' }}>
                        Souhrn obsahuje veškerou detailní analýzu smlouvy
                      </Typography>
                    </Box>
                    <Box sx={{ flex: '1 1 220px', textAlign: 'center', p: 1.5, bgcolor: 'rgba(255,255,255,0.6)', borderRadius: 2 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#d32f2f', mb: 0.5, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                        <KeyIcon sx={{ fontSize: '1.1rem' }} /> Krok 3: Klíčové podmínky
                      </Typography>
                      <Typography variant="body2" sx={{ fontSize: '0.85rem', color: '#666' }}>
                        Najdete zde nejdůležitější informace a podmínky
                      </Typography>
                    </Box>
                    <Box sx={{ flex: '1 1 220px', textAlign: 'center', p: 1.5, bgcolor: 'rgba(255,255,255,0.6)', borderRadius: 2 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#d32f2f', mb: 0.5, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                        <WarningIcon sx={{ fontSize: '1.1rem' }} /> Krok 4: Rizika
                      </Typography>
                      <Typography variant="body2" sx={{ fontSize: '0.85rem', color: '#666' }}>
                        Detailní přehled všech identifikovaných rizik
                      </Typography>
                    </Box>
                  </Box>
              </CardContent>
            </Card>

            {/* Share Guidance */}
            <Card sx={{ mb: 3, background: 'linear-gradient(135deg, rgba(255,255,255,0.95) 0%, rgba(248,249,252,0.9) 100%)' }}>
              <CardContent sx={{ py: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mb: 1.5 }}>
                  <ShareIcon sx={{ color: 'primary.main' }} />
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#333' }}>
                    Sdílení analýzy (volitelné)
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
                  Chcete zapojit druhou smluvní stranu nebo právního zástupce? Klikněte na ikonu sdílení vedle tlačítka
                  <strong> Revize</strong> a vygenerujte bezpečný odkaz. Příjemce si s tímto odkazem může zobrazit AI analýzu,
                  přidávat poznámky i revize a společně tak dojít k finální podobě smlouvy. Odkaz sdílejte pouze s důvěryhodnými příjemci.
                </Typography>
              </CardContent>
            </Card>

            {/* Tab Navigation */}
            <Box sx={{
                background: 'linear-gradient(180deg, rgba(255,255,255,0.95) 0%, rgba(248,249,252,0.9) 100%)',
                backdropFilter: 'blur(16px)',
                borderRadius: '12px',
                border: '1px solid rgba(0, 0, 0, 0.08)',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)',
                mb: 3,
                overflow: 'hidden'
              }}>
                <Tabs
                  value={activeTab}
                  onChange={(e, newValue) => setActiveTab(newValue)}
                  sx={{
                    '& .MuiTab-root': {
                      color: '#000',
                      fontWeight: 500,
                      fontSize: '0.95rem',
                      textTransform: 'none',
                      minHeight: 56,
                      '&.Mui-selected': {
                        color: '#d32f2f',
                        fontWeight: 600
                      }
                    },
                    '& .MuiTabs-indicator': {
                      backgroundColor: '#d32f2f',
                      height: 3
                    }
                  }}
                >
                  <Tab icon={<FileIcon />} iconPosition="start" label="Náhled smlouvy" />
                  <Tab icon={<AssessmentIcon />} iconPosition="start" label="Rozbor smlouvy" />
                  <Tab icon={<KeyIcon />} iconPosition="start" label="Klíčové podmínky" />
                  <Tab icon={<WarningIcon />} iconPosition="start" label="Rizika" />
                </Tabs>
              </Box>

              {/* TAB 0: Náhled smlouvy */}
              {activeTab === 0 && (
              <Box sx={{
                display: 'flex',
                gap: 3,
                flexDirection: { xs: 'column', lg: 'row' },
                alignItems: 'flex-start'
              }}>

                {/* Left Column - Contract Preview (70%) */}
                <Box sx={{
                  flex: '1 1 70%',
                  maxWidth: { lg: 'calc(70% - 12px)' },
                  width: { xs: '100%', lg: 'calc(70% - 12px)' },
                  minWidth: 0,
                  position: { lg: 'sticky' },
                  top: { lg: 20 },
                  alignSelf: 'flex-start'
                }}>
                  <Card>
                    <CardContent sx={{ p: 2 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1.1rem' }}>
                              📄 Náhled smlouvy
                            </Typography>
                            <Chip
                              label={sideBySide ? 'Dvojitý náhled' : 'Jednoduchý náhled'}
                              variant="outlined"
                              onClick={() => setSideBySide(!sideBySide)}
                              sx={{ cursor: 'pointer' }}
                            />
                          </Box>
                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                          <Tooltip title={isAddingComment ? "Zrušit přidávání poznámky" : "Přidat poznámku k textu"}>
                            <Button
                              startIcon={<AddCommentIcon />}
                              onClick={() => {
                                setIsAddingComment(!isAddingComment);
                                setIsAddingRevision(false);
                                if (!isAddingComment) {
                                  setSelectedText('');
                                  setSelectedTextRange(null);
                                }
                              }}
                              variant="outlined"
                              size="small"
                              sx={{
                                color: '#1976d2 !important',
                                borderColor: '#1976d2',
                                fontWeight: 600,
                                textTransform: 'none',
                                '&:hover': {
                                  borderColor: '#1565c0',
                                  backgroundColor: 'rgba(21,101,192,0.06)'
                                }
                              }}
                            >
                              {isAddingComment ? 'Vyberte text' : 'Poznámka'}
                            </Button>
                          </Tooltip>
                          <Tooltip title={isAddingRevision ? "Zrušit přidávání revize" : "Přidat revizi k textu"}>
                            <Button
                              startIcon={<TrackChangesIcon />}
                              onClick={() => {
                                setIsAddingRevision(!isAddingRevision);
                                setIsAddingComment(false);
                                if (!isAddingRevision) {
                                  setSelectedText('');
                                  setSelectedTextRange(null);
                                }
                              }}
                              variant="outlined"
                              size="small"
                              sx={{
                                color: '#1976d2 !important',
                                borderColor: '#1976d2',
                                fontWeight: 600,
                                textTransform: 'none',
                                '&:hover': {
                                  borderColor: '#1565c0',
                                  backgroundColor: 'rgba(21,101,192,0.06)'
                                }
                              }}
                            >
                              {isAddingRevision ? 'Vyberte text' : 'Revize'}
                            </Button>
                          </Tooltip>
                          <Tooltip title="Sdílet odkaz">
                            <IconButton onClick={() => { setShareLink(null); setShowShareDialog(true); }} size="small" aria-label="Sdílet odkaz">
                              <ShareIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </Box>
                      <Box sx={{ display: sideBySide ? 'flex' : 'block', gap: 2 }}>
                        {sideBySide ? (
                          <>
                            <Box sx={{ minHeight: '500px', maxHeight: '70vh', overflow: 'auto', flex: 1, position: 'relative' }} ref={previewLeftRef}
                              onScroll={() => {
                                if (!previewLeftRef.current || !previewRightRef.current) return;
                                const ratio = previewLeftRef.current.scrollTop / (previewLeftRef.current.scrollHeight - previewLeftRef.current.clientHeight);
                                previewRightRef.current.scrollTop = ratio * (previewRightRef.current.scrollHeight - previewRightRef.current.clientHeight);
                              }}
                            >
                              <ContractPreviewPatch
                                analysisResult={analysisResult}
                                highlightedText={''}
                                comments={[]}
                                revisions={[]}
                                isAddingComment={false}
                                isAddingRevision={false}
                                setSelectedText={setSelectedText}
                                setSelectedTextRange={setSelectedTextRange}
                                setShowCommentDialog={setShowCommentDialog}
                                setShowRevisionDialog={setShowRevisionDialog}
                                setNewRevisionText={setNewRevisionText}
                                mdxEditorRef={mdxEditorRef}
                                resolvedRiskIds={resolvedRisks}
                                riskRanges={[]}
                                onQuickSelect={(p) => setQuickSelect({ text: p.text, start: p.start, end: p.end, x: p.rect.x, y: p.rect.y, visible: true })}
                              />
                            </Box>
                            <Box sx={{ minHeight: '500px', maxHeight: '70vh', overflow: 'auto', flex: 1, position: 'relative' }} ref={previewRightRef}
                              onScroll={() => {
                                if (!previewLeftRef.current || !previewRightRef.current) return;
                                const ratio = previewRightRef.current.scrollTop / (previewRightRef.current.scrollHeight - previewRightRef.current.clientHeight);
                                previewLeftRef.current.scrollTop = ratio * (previewLeftRef.current.scrollHeight - previewLeftRef.current.clientHeight);
                              }}
                            >
                              <ContractPreviewPatch
                                analysisResult={analysisResult}
                                highlightedText={highlightedText}
                                comments={comments}
                                revisions={revisions}
                                isAddingComment={isAddingComment}
                                isAddingRevision={isAddingRevision}
                                setSelectedText={setSelectedText}
                                setSelectedTextRange={setSelectedTextRange}
                                setShowCommentDialog={setShowCommentDialog}
                                setShowRevisionDialog={setShowRevisionDialog}
                                setNewRevisionText={setNewRevisionText}
                                mdxEditorRef={mdxEditorRef}
                                resolvedRiskIds={resolvedRisks}
                                riskRanges={riskHighlightRanges}
                                onQuickSelect={(p) => setQuickSelect({ text: p.text, start: p.start, end: p.end, x: p.rect.x, y: p.rect.y, visible: true })}
                              />
                            </Box>
                          </>
                        ) : (
                          <Box sx={{ minHeight: '500px', maxHeight: '70vh', overflow: 'auto', position: 'relative' }} ref={contractPreviewRef}>
                            <ContractPreviewPatch
                              analysisResult={analysisResult}
                              highlightedText={highlightedText}
                              comments={comments}
                              revisions={revisions}
                              isAddingComment={isAddingComment}
                              isAddingRevision={isAddingRevision}
                              setSelectedText={setSelectedText}
                              setSelectedTextRange={setSelectedTextRange}
                              setShowCommentDialog={setShowCommentDialog}
                              setShowRevisionDialog={setShowRevisionDialog}
                              setNewRevisionText={setNewRevisionText}
                              mdxEditorRef={mdxEditorRef}
                              resolvedRiskIds={resolvedRisks}
                              riskRanges={riskHighlightRanges}
                              onQuickSelect={(p) => setQuickSelect({ text: p.text, start: p.start, end: p.end, x: p.rect.x, y: p.rect.y, visible: true })}
                            />
                          </Box>
                        )}
                      </Box>
                    </CardContent>
                  </Card>
                </Box>

                {/* Right Column - Comments, Revisions, Risks (30%) */}
                <Box sx={{
                  flex: '1 1 30%',
                  maxWidth: { lg: 'calc(30% - 12px)' },
                  width: { xs: '100%', lg: 'calc(30% - 12px)' },
                  minWidth: 0
                }}>
                  <Stack spacing={2}>
                    {/* Comments and Revisions Card */}
                    <Card>
                      <CardContent>
                        <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                          Poznámky a revize
                        </Typography>
                        {(comments.length > 0 || revisions.length > 0) ? (
                          <Stack spacing={3}>
                            {/* Comments */}
                            {comments.length > 0 && (
                              <Box>
                                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: '#ffa726' }}>
                                  💬 Poznámky ({comments.length})
                                </Typography>
                                <Stack spacing={1}>
                                  {comments.map((comment) => (
                                    <Paper
                                      key={comment.id}
                                      sx={{ p: 1.5, cursor: 'pointer', position: 'relative', '&:hover': { bgcolor: 'rgba(255, 193, 7, 0.05)' } }}
                                      onClick={() => handleCommentClick(comment)}
                                    >
                                      <IconButton
                                        size="small"
                                        onClick={(e) => { e.stopPropagation(); handleDeleteComment(comment.id); }}
                                        sx={{ position: 'absolute', top: 4, right: 4 }}
                                      >
                                        <CloseIcon fontSize="small" />
                                      </IconButton>
                                      <Typography variant="caption" color="text.secondary">
                                        {new Date(comment.timestamp).toLocaleString()}
                                      </Typography>
                                      <Typography variant="body2" sx={{ mt: 0.5 }}>{comment.comment}</Typography>
                                    </Paper>
                                  ))}
                                </Stack>
                              </Box>
                            )}

                            {/* Revisions */}
                            {revisions.length > 0 && (
                              <Box>
                                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: '#42a5f5' }}>
                                  ✏️ Revize ({revisions.length})
                                </Typography>
                                <Stack spacing={1}>
                                  {revisions.map((revision) => (
                                    <Paper
                                      key={revision.id}
                                      sx={{ p: 1.5, cursor: 'pointer', position: 'relative', '&:hover': { bgcolor: 'rgba(33, 150, 243, 0.05)' } }}
                                      onClick={() => handleRevisionClick(revision)}
                                    >
                                      <IconButton
                                        size="small"
                                        onClick={(e) => { e.stopPropagation(); handleDeleteRevision(revision.id); }}
                                        sx={{ position: 'absolute', top: 4, right: 4 }}
                                      >
                                        <CloseIcon fontSize="small" />
                                      </IconButton>
                                      <Typography variant="caption" color="text.secondary">
                                        {new Date(revision.timestamp).toLocaleString()}
                                      </Typography>
                                      <Typography variant="body2" sx={{ mt: 0.5 }}>
                                        <Box component="span" sx={{ textDecoration: 'line-through', color: '#d32f2f' }}>
                                          {revision.originalText.substring(0, 40)}...
                                        </Box>
                                        {' → '}
                                        <Box component="span" sx={{ color: '#2e7d32', fontWeight: 500 }}>
                                          {revision.newText}
                                        </Box>
                                      </Typography>
                                    </Paper>
                                  ))}
                                </Stack>
                              </Box>
                            )}
                          </Stack>
                        ) : (
                          <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 3 }}>
                            Zatím žádné poznámky ani revize
                          </Typography>
                        )}
                      </CardContent>
                    </Card>

                    {/* Risks Card under notes & revisions */}
                    {analysisResult?.risk_factors?.length > 0 && (
                      <Card>
                        <CardContent>
                          <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                            ⚠️ Rizika ({analysisResult.risk_factors.length})
                          </Typography>
                          <Stack spacing={1.5}>
                            {analysisResult.risk_factors.map((risk, index) => (
                              <Paper
                                key={index}
                                sx={{ p: 1.5, cursor: 'pointer', '&:hover': { bgcolor: 'rgba(255, 193, 7, 0.06)' }, position: 'relative' }}
                                onClick={() => handleRiskClick(risk as any, index)}
                              >
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                      {risk.category}
                                    </Typography>
                                    {(resolvedRisks.has(`risk-${index}`) || (risk as any).resolvedByRevision) && (
                                      <TaskAltIcon sx={{ color: '#2e7d32' }} titleAccess="Riziko vyřešeno revizí" />
                                    )}
                                  </Box>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <Chip
                                      size="small"
                                      label={risk.severity === 'high' ? 'VYSOKÉ' : risk.severity === 'medium' ? 'STŘEDNÍ' : 'NÍZKÉ'}
                                      sx={{
                                        bgcolor: risk.severity === 'high' ? '#f57c00' : risk.severity === 'medium' ? '#fbc02d' : '#388e3c',
                                        color: '#fff',
                                        fontWeight: 700
                                      }}
                                    />
                                    <Tooltip
                                      title={
                                        <Box sx={{ p: 0.5 }}>
                                          <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', mb: 0.5 }}>
                                            Proč je to riziko?
                                          </Typography>
                                          <Typography variant="caption" sx={{ display: 'block', whiteSpace: 'normal' }}>
                                            {(risk as any).description || 'Rizikový faktor identifikován při analýze.'}
                                          </Typography>
                                          {(risk as any).clause_text && (
                                            <Typography variant="caption" sx={{ display: 'block', whiteSpace: 'normal', mt: 0.5, color: 'text.secondary' }}>
                                              Citace: „{((risk as any).clause_text as string).length > 200 ? ((risk as any).clause_text as string).slice(0, 200) + '…' : (risk as any).clause_text}“
                                            </Typography>
                                          )}
                                        </Box>
                                      }
                                      arrow
                                      placement="top"
                                    >
                                      <IconButton size="small" onClick={(e) => e.stopPropagation()}>
                                        <HelpOutlineIcon fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Opraví riziko pomocí AI" arrow>
                                      <IconButton size="small" aria-label="oprava rizika AI" onClick={(e) => { e.stopPropagation(); handleSuggestFix(risk as any, index); }}>
                                        <AutoFixHighIcon fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                  </Box>
                                </Box>
                                {risk.clause_text && (
                                  <Typography variant="body2" sx={{ mt: 0.5, color: 'text.secondary' }}>
                                    „{risk.clause_text.substring(0, 100)}{risk.clause_text.length > 100 ? '…' : ''}“
                                  </Typography>
                                )}
                                {/* help icon moved next to AI action for consistent alignment */}
                              </Paper>
                            ))}
                          </Stack>
                        </CardContent>
                      </Card>
                    )}
                  </Stack>
                </Box>

              </Box>
              )}              {/* TAB 1: Rozbor smlouvy */}
              {activeTab === 1 && (
                <Box sx={{
                  display: 'flex',
                  gap: 3,
                  flexDirection: { xs: 'column', lg: 'row' },
                  alignItems: 'flex-start'
                }}>
                  {/* Left Column - Content (70%) */}
                  <Box sx={{
                    flex: '1 1 70%',
                    maxWidth: { lg: 'calc(70% - 12px)' },
                    width: { xs: '100%', lg: 'calc(70% - 12px)' },
                    minWidth: 0
                  }}>
                    <Card sx={{ mb: 3 }}>
                      <CardContent>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                          <Typography variant="h6" sx={{ fontWeight: 600 }}>
                            📊 Celkové zhodnocení
                          </Typography>
                          <Chip 
                            size="small"
                            variant="outlined"
                        label={`Zvolená strana: ${userRole === 'first' ? 'První strana' : 'Druhá strana'}`}
                            sx={{ color: 'text.secondary' }}
                          />
                        </Box>
                        <Box sx={{
                          '& h2': {
                            fontSize: '1.25rem',
                            fontWeight: 600,
                            mb: 2,
                            mt: 3,
                            color: '#333333 !important',
                            '&:first-of-type': { mt: 0 }
                          },
                          '& h3': {
                            fontSize: '1.1rem',
                            fontWeight: 600,
                            mb: 1,
                            mt: 2,
                            color: '#333333 !important',
                            scrollMarginTop: '80px'
                          },
                          '& p': {
                            mb: 1.5,
                            color: '#333333 !important',
                            lineHeight: 1.7
                          },
                          '& ul, & ol': {
                            pl: 2,
                            mb: 2
                          },
                          '& li': {
                            mb: 0.5,
                            color: '#333333 !important'
                          },
                          '& hr': {
                            my: 3,
                            borderColor: 'divider'
                          },
                          '& strong': {
                            fontWeight: 600,
                            color: '#333333 !important'
                          }
                        }}>
                          <ReactMarkdown
                            components={{
                              h3: ({node, children, ...props}) => {
                                const text = children?.toString() || '';
                                const id = text.toLowerCase().replace(/\s+/g, '-').replace(/[^\w-]/g, '');
                                return <h3 id={id} {...props}>{children}</h3>;
                              }
                            }}
                          >
                            {analysisResult.overall_assessment}
                          </ReactMarkdown>
                        </Box>

                        {analysisResult.recommendations.length > 0 && (
                          <Box sx={{ mt: 3, pt: 3, borderTop: '2px solid', borderColor: 'divider' }}>
                            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: '#333333 !important' }}>
                              💡 Doporučení ({analysisResult.recommendations.length})
                            </Typography>
                            <List>
                              {analysisResult.recommendations.map((recommendation, index) => (
                                <ListItem key={index}>
                                  <ListItemIcon>
                                    <CheckIcon sx={{ color: '#4caf50' }} />
                                  </ListItemIcon>
                                  <ListItemText
                                    primary={
                                      <Typography variant="body1" sx={{ color: '#333333 !important' }}>
                                        {recommendation}
                                      </Typography>
                                    }
                                  />
                                </ListItem>
                              ))}
                            </List>
                          </Box>
                        )}
                      </CardContent>
                    </Card>
                  </Box>

                  {/* Right Column - Table of Contents (30%) */}
                  <Box sx={{
                    flex: '1 1 30%',
                    maxWidth: { lg: 'calc(30% - 12px)' },
                    width: { xs: '100%', lg: 'calc(30% - 12px)' },
                    minWidth: 0,
                    position: { lg: 'sticky' },
                    top: { lg: 20 },
                    alignSelf: 'flex-start'
                  }}>
                    <Card>
                      <CardContent>
                        <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                          📑 Obsah
                        </Typography>
                        <Box sx={{ pl: 0 }}>
                          {(() => {
                            const headings = analysisResult.overall_assessment.match(/^###\s+(.+)$/gm) || [];
                            return headings.length > 0 ? (
                              <List dense>
                                {headings.map((heading, index) => {
                                  const text = heading.replace(/^###\s+/, '');
                                  const id = text.toLowerCase().replace(/\s+/g, '-').replace(/[^\w-]/g, '');
                                  const isActive = activeHeadingId === id;
                                  return (
                                    <ListItem
                                      key={index}
                                      sx={{
                                        py: 0.5,
                                        px: 1,
                                        cursor: 'pointer',
                                        borderRadius: 1,
                                        transition: 'all 0.2s',
                                        bgcolor: isActive ? 'rgba(211, 47, 47, 0.12)' : 'transparent',
                                        borderLeft: isActive ? '3px solid #d32f2f' : '3px solid transparent',
                                        pl: isActive ? 1.5 : 1,
                                        '&:hover': {
                                          bgcolor: isActive ? 'rgba(211, 47, 47, 0.12)' : 'rgba(211, 47, 47, 0.08)',
                                          pl: 1.5
                                        }
                                      }}
                                      onClick={() => {
                                        const element = document.getElementById(id);
                                        if (element) {
                                          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
                                        }
                                      }}
                                    >
                                      <ListItemText
                                        primary={text}
                                        primaryTypographyProps={{
                                          variant: 'body2',
                                          sx: {
                                            color: isActive ? '#d32f2f' : '#333',
                                            fontWeight: isActive ? 600 : 500,
                                            fontSize: '0.875rem'
                                          }
                                        }}
                                      />
                                    </ListItem>
                                  );
                                })}
                              </List>
                            ) : (
                              <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                                Žádné nadpisy k zobrazení
                              </Typography>
                            );
                          })()}
                        </Box>
                      </CardContent>
                    </Card>
                  </Box>
                </Box>
              )}

              {/* TAB 2: Klíčové podmínky */}
              {activeTab === 2 && analysisResult.key_terms.length > 0 && (
                <Card sx={{ mb: 3 }}>
                  <CardContent>
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                      🔑 Klíčové podmínky ({analysisResult.key_terms.length})
                    </Typography>
                    <List>
                      {analysisResult.key_terms.map((term, index) => (
                        <React.Fragment key={index}>
                          <ListItem>
                            <ListItemIcon>
                              <InfoIcon color={term.is_standard ? 'success' : 'warning'} />
                            </ListItemIcon>
                            <ListItemText
                              primary={
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                    {term.category}
                                  </Typography>
                                  <Typography variant="body1">
                                    {term.value}
                                  </Typography>
                                </Box>
                              }
                              secondary={
                                <React.Fragment>
                                  <Typography variant="body2" sx={{ mt: 1 }}>
                                    {term.description}
                                  </Typography>
                                  {term.recommendation && (
                                    <Typography variant="body2" sx={{ fontWeight: 600, color: 'primary.main', mt: 1 }}>
                                      💡 {term.recommendation}
                                    </Typography>
                                  )}
                                </React.Fragment>
                              }
                              secondaryTypographyProps={{ component: 'div' }}
                            />
                          </ListItem>
                          {index < analysisResult.key_terms.length - 1 && <Divider />}
                        </React.Fragment>
                      ))}
                    </List>
                  </CardContent>
                </Card>
              )}

              {/* TAB 3: Rizika */}
              {activeTab === 3 && analysisResult.risk_factors.length > 0 && (
                <Card sx={{ mb: 3 }}>
                  <CardContent>
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
                      ⚠️ Identifikovaná rizika ({analysisResult.risk_factors.length})
                    </Typography>
                    <Stack spacing={2}>
                      {analysisResult.risk_factors.map((risk, index) => (
                        <Paper
                          key={index}
                          sx={{
                            p: 2.5,
                            cursor: 'pointer',
                            transition: 'all 0.2s',
                            '&:hover': {
                              bgcolor: 'rgba(244, 67, 54, 0.05)',
                              boxShadow: 2
                            },
                            position: 'relative'
                          }}
                          onClick={() => handleRiskClick(risk, index)}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                              <WarningIcon sx={{ color: risk.severity === 'high' ? '#f57c00' : risk.severity === 'medium' ? '#fbc02d' : '#388e3c' }} />
                              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                {risk.category}
                              </Typography>
                              {(resolvedRisks.has(`risk-${index}`) || (risk as any).resolvedByRevision) && (
                                <TaskAltIcon sx={{ color: '#2e7d32' }} titleAccess="Riziko vyřešeno revizí" />
                              )}
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <Chip
                                label={risk.severity === 'high' ? 'VYSOKÉ RIZIKO' : risk.severity === 'medium' ? 'STŘEDNÍ RIZIKO' : 'NÍZKÉ RIZIKO'}
                                sx={{
                                  bgcolor: risk.severity === 'high' ? '#f57c00' : risk.severity === 'medium' ? '#fbc02d' : '#388e3c',
                                  color: '#fff',
                                  fontWeight: 600,
                                  fontSize: '0.75rem'
                                }}
                              />
                              <Tooltip
                                title={
                                  <Box sx={{ p: 0.5 }}>
                                    <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', mb: 0.5 }}>
                                      Proč je to riziko?
                                    </Typography>
                                    <Typography variant="caption" sx={{ display: 'block', whiteSpace: 'normal' }}>
                                      {risk.description || 'Rizikový faktor identifikován při analýze.'}
                                    </Typography>
                                    {risk.clause_text && (
                                      <Typography variant="caption" sx={{ display: 'block', whiteSpace: 'normal', mt: 0.5, color: 'text.secondary' }}>
                                        Citace: „{risk.clause_text.length > 200 ? risk.clause_text.slice(0, 200) + '…' : risk.clause_text}“
                                      </Typography>
                                    )}
                                  </Box>
                                }
                                arrow
                                placement="top"
                              >
                                <IconButton size="small" onClick={(e) => e.stopPropagation()}>
                                  <HelpOutlineIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Opraví riziko pomocí AI" arrow>
                                <IconButton size="small" aria-label="oprava rizika AI" onClick={(e) => { e.stopPropagation(); handleSuggestFix(risk, index); }}>
                                  <AutoFixHighIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          </Box>
                          <Typography variant="body1" sx={{ mb: 1.5, lineHeight: 1.6 }}>
                            {risk.description}
                          </Typography>
                          {risk.recommendation && (
                            <Box sx={{
                              p: 1.5,
                              bgcolor: 'rgba(33, 150, 243, 0.08)',
                              borderRadius: 1,
                              borderLeft: '3px solid #2196f3'
                            }}>
                              <Typography variant="body2" sx={{ fontWeight: 600, color: '#1976d2' }}>
                                💡 Doporučení: {risk.recommendation}
                              </Typography>
                            </Box>
                          )}
                          {/* help icon aligned in header; bottom-right removed */}
                        </Paper>
                      ))}
                    </Stack>
                  </CardContent>
                </Card>
              )}

              {/* Variables Panel removed as requested */}

            </Box>
          </Fade>
        )}

        {/* Floating Selection Toolbar */}
        <SelectionToolbar
          open={Boolean(quickSelect?.visible)}
          anchor={quickSelect ? { x: quickSelect.x, y: quickSelect.y } : null}
          onClose={() => setQuickSelect(null)}
          onAddComment={openQuickComment}
          onAddRevision={openQuickRevision}
          onRephrase={openQuickRephrase}
          onCopy={quickSelect?.text ? () => navigator.clipboard.writeText(quickSelect.text) : undefined}
        />

        {/* Info */}
        <Alert severity="info" sx={{ mt: 4 }}>
          <Typography variant="body2">
            <strong>Právní upozornění:</strong> Tato analýza slouží pouze pro informační účely.
            Pro konkrétní právní záležitosti vždy konzultujte kvalifikovaného advokáta.
          </Typography>
        </Alert>
      
      {/* Snackbar pro notifikaci o zkopírování odkazu */}
      <Snackbar
        open={linkCopied}
        autoHideDuration={3000}
        onClose={() => setLinkCopied(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setLinkCopied(false)} severity="success" role="alert" aria-live="polite" sx={{ width: '100%' }}>
          Odkaz zkopírován do schránky
        </Alert>
      </Snackbar>
        </Box>
      </Box>
      
      {/* Analyzing Dialog */}
      <Dialog
        open={isAnalyzing}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '20px',
            background: 'rgba(255, 255, 255, 0.98)',
            backdropFilter: 'blur(20px)',
            WebkitBackdropFilter: 'blur(20px)',
            border: '1px solid rgba(255, 255, 255, 0.5)',
            boxShadow: '0 24px 48px rgba(0, 0, 0, 0.12)',
            overflow: 'hidden'
          }
        }}
      >
        <DialogContent sx={{ p: 0 }}>
          <Box sx={{ 
            position: 'relative',
            overflow: 'hidden'
          }}>
            {/* Animated background */}
            <Box sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: '200px',
              background: 'linear-gradient(135deg, #1F1F1F 0%, #333333 100%)',
              opacity: 0.05,
              animation: 'pulse 2s ease-in-out infinite'
            }} />
            
            <Box sx={{ 
              position: 'relative',
              p: 5,
              textAlign: 'center'
            }}>
              {/* Animated Icon */}
              <Grow in={isAnalyzing} timeout={500}>
                <Avatar sx={{
                  width: 100,
                  height: 100,
                  mx: 'auto',
                  mb: 3,
                  background: 'linear-gradient(135deg, rgba(255,255,255,0.95) 0%, rgba(255,255,255,0.85) 100%)',
                  backdropFilter: 'blur(16px)',
                  WebkitBackdropFilter: 'blur(16px)',
                  border: '1px solid rgba(255,255,255,0.4)',
                  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.06)',
                  animation: 'float 3s ease-in-out infinite',
                  '& svg': {
                    color: 'var(--liquid-gray-700)',
                    fontSize: 50
                  }
                }}>
                  <SecurityIcon />
                </Avatar>
              </Grow>

              <Typography variant="h5" sx={{ 
                fontWeight: 700,
                mb: 2,
                color: 'var(--liquid-gray-800)'
              }}>
                Provádím analýzu dokumentu
              </Typography>

              <Typography variant="body1" sx={{ 
                mb: 4,
                color: 'var(--liquid-gray-600)',
                minHeight: '24px'
              }}>
                {analysisPhase}
              </Typography>

              {/* Progress Bar */}
              <Box sx={{ mb: 3 }}>
                <Box sx={{ 
                  position: 'relative',
                  height: 8,
                  borderRadius: 9999,
                  background: 'rgba(0, 0, 0, 0.06)',
                  overflow: 'hidden'
                }}>
                  <Box sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    height: '100%',
                    width: `${analysisProgress}%`,
                    background: '#d32f2f',
                    borderRadius: 9999,
                    transition: 'width 0.5s ease',
                    boxShadow: '0 2px 8px rgba(211, 47, 47, 0.3)'
                  }} />
                </Box>
                <Typography variant="caption" sx={{ 
                  mt: 1,
                  display: 'block',
                  color: 'var(--liquid-gray-500)'
                }}>
                  {Math.round(analysisProgress)}% dokončeno
                </Typography>
              </Box>

              {/* Analysis Steps */}
              <Stack spacing={1} sx={{ 
                mt: 4,
                textAlign: 'left',
                maxWidth: 400,
                mx: 'auto'
              }}>
                {[
                  { label: 'Extrakce celého dokumentu', done: analysisProgress > 15 },
                  { label: 'Identifikace rizik a úskalí', done: analysisProgress > 30 },
                  { label: 'Analýza asymetrií', done: analysisProgress > 45 },
                  { label: 'Právní compliance', done: analysisProgress > 60 },
                  { label: 'Finanční dopady', done: analysisProgress > 75 },
                  { label: 'Akční plán a doporučení', done: analysisProgress > 90 }
                ].map((step, index) => (
                  <Fade in key={index} timeout={500 * (index + 1)}>
                    <Box sx={{ 
                      display: 'flex',
                      alignItems: 'center',
                      gap: 2,
                      opacity: step.done ? 1 : 0.4,
                      transition: 'opacity 0.4s ease-out'
                    }}>
                      {step.done ? (
                        <CheckIcon sx={{ color: '#4caf50', fontSize: 20 }} />
                      ) : (
                        <CircularProgress size={20} sx={{ color: 'var(--liquid-gray-400)' }} />
                      )}
                      <Typography variant="body2" sx={{ 
                        color: step.done ? 'var(--liquid-gray-700)' : 'var(--liquid-gray-500)',
                        fontWeight: step.done ? 600 : 400
                      }}>
                        {step.label}
                      </Typography>
                    </Box>
                  </Fade>
                ))}
              </Stack>

              <Typography variant="caption" sx={{ 
                mt: 4,
                display: 'block',
                color: 'var(--liquid-gray-500)'
              }}>
                Vydržte prosím, analýza může trvat až 30 sekund...
              </Typography>
            </Box>
          </Box>

          <style>
            {`
              @keyframes float {
                0%, 100% { transform: translateY(0px); }
                50% { transform: translateY(-10px); }
              }
              @keyframes pulse {
                0%, 100% { opacity: 0.8; }
                50% { opacity: 1; }
              }
              @keyframes blink {
                0%, 100% { box-shadow: 0 0 10px rgba(255, 193, 7, 0.6); }
                50% { box-shadow: 0 0 20px rgba(255, 193, 7, 1); }
              }
              @keyframes revision-pulse {
                0% {
                  box-shadow: 0 0 0 0 rgba(255, 193, 7, 0.7);
                }
                50% {
                  box-shadow: 0 0 10px 5px rgba(255, 193, 7, 0.3);
                }
                100% {
                  box-shadow: 0 0 0 0 rgba(255, 193, 7, 0);
                }
              }
              
              .revision-highlight-pulse.revision-strikethrough {
                animation: revision-pulse 2s ease-in-out;
                background-color: rgba(255, 193, 7, 0.3) !important;
              }
              
              .revision-highlight-pulse.revision-replacement {
                animation: revision-pulse 2s ease-in-out;
                background-color: rgba(255, 193, 7, 0.3) !important;
              }
            `}
          </style>
        </DialogContent>
      </Dialog>

      {/* Dialog: AI návrh opravy rizika */}
      <Dialog
        open={fixDialogOpen}
        onClose={() => setFixDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogContent>
          <Box sx={{ py: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 1 }}>
              <AutoFixHighIcon sx={{ color: 'primary.main' }} />
              <Typography variant="h6">AI návrh opravy rizika</Typography>
            </Box>
            {fixLoading && (
              <Box sx={{ py: 4, textAlign: 'center' }}>
                <CircularProgress size={28} />
                <Typography variant="body2" sx={{ mt: 1 }}>Generuji návrh…</Typography>
              </Box>
            )}
            {!fixLoading && fixError && (
              <Alert severity="error" sx={{ mb: 2 }}>{fixError}</Alert>
            )}
            {!fixLoading && !fixError && (
              <>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>Navržené nové znění klauzule:</Typography>
                <Paper sx={{ p: 2, mb: 2, bgcolor: '#e8f5e9' }}>
                  <Typography variant="body2" sx={{ color: '#2e7d32', whiteSpace: 'pre-wrap' }}>{fixText}</Typography>
                </Paper>
                {fixReason && (
                  <>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>Důvod úpravy:</Typography>
                    <Paper sx={{ p: 2, mb: 2 }}>
                      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{fixReason}</Typography>
                    </Paper>
                  </>
                )}
                <Alert severity="info" sx={{ mb: 2 }}>
                  Toto je pouze obecná právní informace. Před podpisem doporučujeme konzultaci s advokátem.
                </Alert>
                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                  <Button variant="outlined" onClick={() => setFixDialogOpen(false)} sx={{ color: '#000 !important' }}>Zavřít</Button>
                  <Button variant="contained" onClick={applySuggestedFix} sx={{ color: '#000 !important' }} disabled={!fixText}>Schválit a použít</Button>
                </Box>
              </>
            )}
          </Box>
        </DialogContent>
      </Dialog>

      {/* Dialog pro přidání poznámky */}
      <Dialog 
        open={showCommentDialog} 
        onClose={() => {
          setShowCommentDialog(false);
          setNewCommentText('');
          setEditingComment(null);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogContent>
          <Typography variant="h6" gutterBottom>
            {editingComment ? 'Upravit poznámku' : 'Přidat poznámku'}
          </Typography>
          <Typography variant="body2" color="textSecondary" gutterBottom>
            Vybraný text: "{selectedText?.substring(0, 100)}{selectedText?.length > 100 ? '...' : ''}"
          </Typography>
          <TextField
            margin="dense"
            label="Vaše poznámka"
            fullWidth
            multiline
            rows={4}
            variant="outlined"
            value={newCommentText}
            onChange={(e) => setNewCommentText(e.target.value)}
          />
          <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between' }}>
            <Button 
              onClick={() => {
                setShowCommentDialog(false);
                setNewCommentText('');
                setSelectedText('');
                setSelectedTextRange(null);
                // Necháme aktivní režim přidávání poznámky pro nový výběr
                setIsAddingComment(true);
              }}
              sx={{ 
                color: '#000 !important',
                borderColor: '#666',
                '&:hover': {
                  borderColor: '#000',
                  backgroundColor: 'rgba(0,0,0,0.04)'
                }
              }}
              variant="outlined"
            >
              Upravit výběr
            </Button>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button 
                onClick={() => {
                  setShowCommentDialog(false);
                  setNewCommentText('');
                  setIsAddingComment(false);
                }}
                sx={{ 
                  color: '#000 !important',
                  '&:hover': {
                    backgroundColor: 'rgba(0,0,0,0.04)'
                  }
                }}
              >
                Zrušit
              </Button>
              <Button 
                variant="contained" 
                onClick={() => {
                  if (!newCommentText.trim()) return;
                  if (editingComment) {
                    const updated = comments.map(c => c.id === editingComment ? {
                      ...c,
                      comment: newCommentText,
                      text: newCommentText
                    } : c);
                    setComments(updated);
                    setEditingComment(null);
                    setShowCommentDialog(false);
                    setNewCommentText('');
                  } else {
                    if (selectedTextRange) {
                      const newComment = {
                        id: Date.now().toString(),
                        text: newCommentText,
                        selectedText: selectedText,
                        start: selectedTextRange.start,
                        end: selectedTextRange.end,
                        comment: newCommentText,
                        timestamp: new Date()
                      };
                      setComments([...comments, newComment]);
                      setShowCommentDialog(false);
                      setNewCommentText('');
                      setIsAddingComment(false);
                      setSelectedText('');
                      setSelectedTextRange(null);
                    }
                  }
                }}
                disabled={!newCommentText.trim()}
                sx={{
                  backgroundColor: '#424242',
                  color: '#fff !important',
                  '&:hover': {
                    backgroundColor: '#333'
                  },
                  '&:disabled': {
                    backgroundColor: '#ccc',
                    color: '#999 !important'
                  }
                }}
              >
                {editingComment ? 'Uložit' : 'Přidat'}
              </Button>
            </Box>
          </Box>
        </DialogContent>
      </Dialog>

      {/* Dialog pro přidání revize */}
      <Dialog 
        open={showRevisionDialog} 
        onClose={() => {
          setShowRevisionDialog(false);
          setNewRevisionText('');
          setNewRevisionReason('');
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogContent>
          <Typography variant="h6" gutterBottom sx={{ color: '#000' }}>
            Přidat revizi textu
          </Typography>
          <Typography variant="body2" sx={{ color: '#666', mb: 2 }}>
            Původní text:
          </Typography>
          <Box sx={{ 
            p: 2, 
            bgcolor: '#f5f5f5', 
            borderRadius: 1, 
            mb: 2,
            maxHeight: 150,
            overflow: 'auto',
            border: '1px solid #e0e0e0'
          }}>
            <Typography variant="body2" sx={{ color: '#000' }}>
              {selectedText}
            </Typography>
          </Box>
          
          <TextField
            margin="dense"
            label="Nový text (nahrazení)"
            fullWidth
            multiline
            rows={3}
            variant="outlined"
            value={newRevisionText}
            onChange={(e) => setNewRevisionText(e.target.value)}
            sx={{ mb: 2 }}
          />
          
          <TextField
            margin="dense"
            label="Důvod změny"
            fullWidth
            multiline
            rows={2}
            variant="outlined"
            value={newRevisionReason}
            onChange={(e) => setNewRevisionReason(e.target.value)}
            placeholder="Např. Nevýhodná formulace, doporučuji změnit..."
          />
          
          <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between' }}>
            <Button 
              onClick={() => {
                setShowRevisionDialog(false);
                setNewRevisionText('');
                setNewRevisionReason('');
                setSelectedText('');
                setSelectedTextRange(null);
                setIsAddingRevision(true);
              }}
              sx={{ 
                color: '#1976d2 !important',
                borderColor: '#1976d2',
                fontWeight: 600,
                textTransform: 'none',
                '&:hover': {
                  borderColor: '#1565c0',
                  backgroundColor: 'rgba(21,101,192,0.06)'
                }
              }}
              variant="outlined"
            >
              Upravit výběr
            </Button>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button 
                onClick={() => {
                  setShowRevisionDialog(false);
                  setNewRevisionText('');
                  setNewRevisionReason('');
                  setIsAddingRevision(false);
                }}
                sx={{ 
                  color: '#000 !important',
                  '&:hover': {
                    backgroundColor: 'rgba(0,0,0,0.04)'
                  }
                }}
              >
                Zrušit
              </Button>
              <Button 
                variant="contained" 
                onClick={() => {
                  if (selectedTextRange && newRevisionText.trim() && newRevisionReason.trim()) {
                    if (editingComment) {
                      // Upravujeme existující revizi
                      const updatedRevisions = revisions.map(r =>
                        r.id === editingComment ? {
                          ...r,
                          newText: newRevisionText,
                          reason: newRevisionReason
                        } : r
                      );
                      setRevisions(updatedRevisions);
                      setEditingComment(null);
                    } else {
                      // Vytváříme novou revizi
                      const newRevision = {
                        id: Date.now().toString(),
                        originalText: selectedText,
                        newText: newRevisionText,
                        reason: newRevisionReason,
                        start: selectedTextRange.start,
                        end: selectedTextRange.end,
                        timestamp: new Date(),
                        accepted: null,
                        authorSource: 'original' as const
                      };
                      setRevisions([...revisions, newRevision]);
                    }
                    setShowRevisionDialog(false);
                    setNewRevisionText('');
                    setNewRevisionReason('');
                    setIsAddingRevision(false);
                    setSelectedText('');
                    setSelectedTextRange(null);
                  }
                }}
                disabled={!newRevisionText.trim() || !newRevisionReason.trim()}
                sx={{
                  py: 1,
                  px: 2.5,
                  fontWeight: 600,
                  textTransform: 'none',
                  backgroundColor: '#1F1F1F !important',
                  bgcolor: '#1F1F1F !important',
                  color: '#FFFFFF !important',
                  border: 'none !important',
                  boxShadow: '0 4px 12px rgba(31, 31, 31, 0.15)',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    backgroundColor: '#e72f0d !important',
                    bgcolor: '#e72f0d !important',
                    transform: 'translateY(-1px)',
                    boxShadow: '0 6px 16px rgba(231, 47, 13, 0.25)'
                  },
                  '&:disabled': {
                    backgroundColor: '#ccc !important',
                    bgcolor: '#ccc !important',
                    color: '#999 !important',
                    boxShadow: 'none',
                    transform: 'none'
                  }
                }}
              >
                Přidat revizi
              </Button>
            </Box>
          </Box>
        </DialogContent>
      </Dialog>

      {/* Potvrzovací dialog pro smazání poznámky/revize */}
      <Dialog 
        open={deleteTarget !== null} 
        onClose={() => setDeleteTarget(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogContent>
          <Typography variant="h6" gutterBottom sx={{ color: '#000' }}>
            {deleteTarget?.kind === 'revision' ? 'Smazat revizi?' : 'Smazat poznámku?'}
          </Typography>
          <Typography variant="body2" sx={{ mb: 3, color: '#666' }}>
            Opravdu chcete smazat tuto {deleteTarget?.kind === 'revision' ? 'revizi' : 'poznámku'}? Tuto akci nelze vrátit zpět.
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
            <Button 
              onClick={() => setDeleteTarget(null)}
              sx={{ 
                color: '#000 !important',
                '&:hover': {
                  backgroundColor: 'rgba(0,0,0,0.04)'
                }
              }}
            >
              Ne
            </Button>
            <Button 
              variant="contained"
              onClick={() => {
                if (!deleteTarget) return;
                if (deleteTarget.kind === 'comment') {
                  setComments(prev => prev.filter(c => c.id !== deleteTarget.id));
                } else {
                  setRevisions(prev => prev.filter(r => r.id !== deleteTarget.id));
                }
                setDeleteTarget(null);
              }}
              sx={{
                backgroundColor: '#f44336',
                color: '#fff !important',
                fontWeight: 600,
                '&:hover': {
                  backgroundColor: '#d32f2f'
                }
              }}
            >
              Ano, smazat
            </Button>
          </Box>
        </DialogContent>
      </Dialog>

      {/* Dialog pro potvrzení přijetí revize */}
      <Dialog 
        open={confirmAcceptRevision !== null} 
        onClose={() => setConfirmAcceptRevision(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogContent>
          <Box sx={{ textAlign: 'center', py: 2 }}>
            <CheckCircleIcon sx={{ fontSize: 48, color: 'success.main', mb: 2 }} />
            <Typography variant="h6" gutterBottom>
              Potvrdit přijetí revize
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Opravdu chcete přijmout tuto revizi? Text bude nahrazen novým.
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
              <Button
                variant="outlined"
                onClick={() => setConfirmAcceptRevision(null)}
                sx={{ color: '#000 !important' }}
              >
                Ne
              </Button>
              <Button
                variant="contained"
                color="success"
                onClick={() => {
                  const updatedRevisions = revisions.map(r =>
                    r.id === confirmAcceptRevision ? { ...r, accepted: true } : r
                  );
                  setRevisions(updatedRevisions);
                  
                  // Check if this revision affects any risks
                  const acceptedRevision = revisions.find(r => r.id === confirmAcceptRevision);
                  if (acceptedRevision && analysisResult?.risk_factors) {
                    const newResolvedRisks = new Set(resolvedRisks);
                    
                    analysisResult.risk_factors.forEach((risk, index) => {
                      // Only check if risk has clause_text and isn't already resolved
                      if (risk.clause_text && !risk.resolvedByRevision) {
                        const riskText = risk.clause_text.trim();
                        const revisionText = acceptedRevision.originalText.trim();
                        
                        // More precise matching: check if the revision text is exactly or mostly the same as risk text
                        // Calculate overlap percentage
                        const minLength = Math.min(riskText.length, revisionText.length);
                        const maxLength = Math.max(riskText.length, revisionText.length);
                        
                        // Check for exact match or significant overlap (>70% of text)
                        let isMatch = false;
                        
                        // 1. Exact match
                        if (riskText === revisionText) {
                          isMatch = true;
                        }
                        // 2. Risk text contains the entire revision text (revision fixes part of the risk)
                        else if (riskText.includes(revisionText) && revisionText.length > 20) {
                          isMatch = true;
                        }
                        // 3. Revision text contains the entire risk text (revision addresses whole risk)
                        else if (revisionText.includes(riskText) && riskText.length > 20) {
                          isMatch = true;
                        }
                        // 4. Check for significant overlap using position matching
                        else if (acceptedRevision.start && acceptedRevision.end && risk.clause_text) {
                          // Get the actual text from the document at revision position
                          const docText = analysisResult.original_text;
                          const revisionOriginalText = docText.substring(acceptedRevision.start, acceptedRevision.end);
                          
                          // Check if this matches the risk's clause text
                          if (revisionOriginalText.toLowerCase().includes(riskText.toLowerCase()) ||
                              riskText.toLowerCase().includes(revisionOriginalText.toLowerCase())) {
                            isMatch = true;
                          }
                        }
                        
                        if (isMatch) {
                          // Mark this specific risk as resolved
                          const riskId = `risk-${index}`;
                          newResolvedRisks.add(riskId);
                          
                          // Update the risk factor with resolution info
                          if (confirmAcceptRevision) {
                            risk.resolvedByRevision = true;
                            risk.revisionId = confirmAcceptRevision;
                          }
                        }
                      }
                    });
                    
                    setResolvedRisks(newResolvedRisks);
                    
                    // Automatically hide resolved risks after accepting revision
                    if (newResolvedRisks.size > resolvedRisks.size) {
                      setHideResolvedRisks(true);
                    }
                  }
                  
                  setConfirmAcceptRevision(null);
                }}
                sx={{ color: '#000 !important' }}
              >
                Ano, přijmout
              </Button>
            </Box>
          </Box>
        </DialogContent>
      </Dialog>

      {/* Dialog pro odmítnutí revize */}
      <Dialog 
        open={rejectRevisionId !== null} 
        onClose={() => {
          setRejectRevisionId(null);
          setRejectReason('');
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogContent>
          <Box sx={{ py: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <CancelIcon sx={{ fontSize: 32, color: 'error.main', mr: 2 }} />
              <Typography variant="h6">
                Odmítnout revizi
              </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Napište důvod, proč odmítáte tuto revizi.
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={3}
              variant="outlined"
              label="Důvod odmítnutí"
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              sx={{ mb: 3 }}
            />
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
              <Button
                variant="outlined"
                onClick={() => {
                  setRejectRevisionId(null);
                  setRejectReason('');
                }}
                sx={{ color: '#000 !important' }}
              >
                Zrušit
              </Button>
              <Button
                variant="contained"
                color="error"
                onClick={() => {
                  if (rejectReason.trim()) {
                    const updatedRevisions = revisions.map(r =>
                      r.id === rejectRevisionId ? { ...r, accepted: false, reason: rejectReason } : r
                    );
                    setRevisions(updatedRevisions);
                    setRejectRevisionId(null);
                    setRejectReason('');
                  }
                }}
                disabled={!rejectReason.trim()}
                sx={{ color: '#000 !important' }}
              >
                Odmítnout
              </Button>
            </Box>
          </Box>
        </DialogContent>
      </Dialog>

      {/* Dialog pro sdílení */}
      <Dialog 
        open={showShareDialog} 
        onClose={() => setShowShareDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogContent>
          <Box sx={{ py: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
              <ShareIcon sx={{ fontSize: 32, color: 'primary.main', mr: 2 }} />
              <Typography variant="h6">
                Sdílení AI analýzy smlouvy
              </Typography>
            </Box>
            {!shareLink && (
              <>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  Vytvořte bezpečný odkaz, který umožní vybraným osobám zobrazit tento AI rozbor smlouvy včetně revizí a poznámek.
                  Odkaz sdílejte pouze s důvěryhodnými příjemci.
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                  <Button
                    variant="outlined"
                    onClick={() => setShowShareDialog(false)}
                    sx={{ color: '#000 !important' }}
                  >
                    Zavřít
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={handleGenerateShareLink}
                    sx={{
                      py: 1,
                      px: 2.5,
                      fontWeight: 600,
                      textTransform: 'none',
                      color: '#1976d2 !important',
                      borderColor: '#1976d2',
                      '&:hover': {
                        borderColor: '#1565c0',
                        backgroundColor: 'rgba(21,101,192,0.06)'
                      }
                    }}
                  >
                    Vytvořit odkaz
                  </Button>
                </Box>
              </>
            )}
            {shareLink && (
              <>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Odkaz byl vytvořen. Sdílejte jej s těmi, kdo mají mít přístup k náhledu analýzy, revizí a poznámek:
                </Typography>
                <Paper sx={{ p: 2, bgcolor: 'grey.50', mb: 3 }}>
                  <Typography variant="body2" sx={{ wordBreak: 'break-all', fontFamily: 'monospace' }}>
                    {shareLink}
                  </Typography>
                </Paper>
                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                  <Button
                    variant="outlined"
                    onClick={() => setShowShareDialog(false)}
                    sx={{ color: '#000 !important' }}
                  >
                    Hotovo
                  </Button>
                  <Button
                    variant="contained"
                    onClick={handleCopyShareLink}
                    sx={{
                      py: 1,
                      px: 2.5,
                      fontWeight: 600,
                      textTransform: 'none',
                      backgroundColor: '#1F1F1F !important',
                      color: '#FFFFFF !important',
                      '&:hover': { backgroundColor: '#e72f0d !important' }
                    }}
                  >
                    Kopírovat odkaz
                  </Button>
                </Box>
              </>
            )}
          </Box>
        </DialogContent>
      </Dialog>

      {/* Dialog pro detail revize */}
      <Dialog 
        open={showRevisionDetail !== null} 
        onClose={() => setShowRevisionDetail(null)}
        maxWidth="md"
        fullWidth
      >
        <DialogContent>
          {showRevisionDetail && (() => {
            const revision = revisions.find(r => r.id === showRevisionDetail);
            if (!revision) return null;
            return (
              <Box sx={{ py: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <TrackChangesIcon sx={{ fontSize: 32, color: '#2196f3' }} />
                  <Typography variant="h6">
                    Detail revize
                  </Typography>
                  {revision.accepted === true && (
                    <Chip label="Přijato" sx={{ bgcolor: '#4caf50', color: '#fff' }} size="small" />
                  )}
                  {revision.accepted === false && (
                    <Chip label="Odmítnuto" sx={{ bgcolor: '#f44336', color: '#fff' }} size="small" />
                  )}
                  {revision.accepted === null && (
                    <Chip label="Čeká" color="default" size="small" />
                  )}
                </Box>
                
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                  Původní text:
                </Typography>
                <Paper sx={{ p: 2, mb: 2, bgcolor: '#ffebee' }}>
                  <Typography variant="body2" sx={{ textDecoration: 'line-through', color: '#d32f2f' }}>
                    {revision.originalText}
                  </Typography>
                </Paper>
                
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                  Nový text:
                </Typography>
                <Paper sx={{ p: 2, mb: 2, bgcolor: '#e8f5e9' }}>
                  <Typography variant="body2" sx={{ color: '#2e7d32' }}>
                    {revision.newText}
                  </Typography>
                </Paper>
                
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                  Důvod změny:
                </Typography>
                <Paper sx={{ p: 2, mb: 2, bgcolor: '#f5f5f5' }}>
                  <Typography variant="body2">
                    {revision.reason}
                  </Typography>
                </Paper>
                
                <Typography variant="caption" color="text.secondary">
                  Vytvořeno: {new Date(revision.timestamp).toLocaleString('cs-CZ')}
                </Typography>
                
                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 3 }}>
                  <Button
                    variant="outlined"
                    onClick={() => setShowRevisionDetail(null)}
                    sx={{ color: '#000 !important' }}
                  >
                    Zavřít
                  </Button>
                </Box>
              </Box>
            );
          })()}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default ContractAnalysisPage;
